import { hashSeed, mulberry32, streamRng } from '../math/rng.js';
import { BODY_TYPES, PAINTS, STEER_MAX, archetypeKey, specFromKey } from '../vehicles/carSpec.js';
import { NO_TRAFFIC_CLASSES, TIER_CLASSES, yawFor } from './LaneGraph.js';

/**
 * @file THE TRAFFIC DIRECTOR: AI cars driving the {@link
 * import('./LaneGraph.js').LaneGraph}, plus the parked rows along the kerbs.
 *
 * A structure-of-arrays manager in the shape of `actors/EnemyManager.js`, and
 * like it, this file imports NO three.js. The `CarFleet` is INJECTED — it is
 * the only piece that needs a GPU, and keeping it out means the entire driving
 * model, the junction protocol and the spawn ring are provable in node
 * (`tests/traffic.test.mjs`). A null fleet is a legal, fully-simulating fleet.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CAR IS AN ARC LENGTH, NOT A RIGID BODY
 * ─────────────────────────────────────────────────────────────────────────────
 * An AI car's POSITION is `sampleAt(edge, s)` — exact, on the lane, forever. Its
 * YAW is a separate, lagging quantity: the direction of a chord CENTRED on the
 * car (`s − yawChord` to `s + yawChord`), rate-limited to
 * {@link TRAFFIC.yawRate}. That split is the whole trick. Integrating a bicycle
 * model per NPC would drift off the lane and need a corrective controller;
 * steering a position that is already correct cannot drift, and the rate limit
 * is what stops a car pivoting on the spot at a junction. The chord being
 * centred matters too: the earlier pure-pursuit aim (`4 + v/2` m ahead) led
 * the tangent at the centre, and every cornering car read as pivoting about
 * its nose (owner playtest round 1). The PLAYER's car is the opposite deal — a
 * real body, in `systems/Driving.js` — because a player must be able to leave
 * the road.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE JUNCTION IS A CLAIM, NOT A RADIUS
 * ─────────────────────────────────────────────────────────────────────────────
 * Crossing a junction means driving a LINK edge (see `LaneGraph.js`), and a link
 * names the node it crosses. One car may hold a node at a time, granted FIFO by
 * ticket — the ticket is stamped when the car first asks, so the queue is the
 * order cars arrived and not the order the SoA happens to be laid out in.
 *
 * Two rules make it live rather than merely safe:
 *  • a claim is only granted when the EXIT LANE has room. "Don't block the box"
 *    is the difference between a busy crossroads and a permanent four-way
 *    gridlock, and it is cheaper than any deadlock detector;
 *  • the claim is released the instant the car leaves the link, not when it is
 *    clear of the paint — a link already ends on the far lane's first metre.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SPEED IS A MINIMUM OF CONSTRAINTS
 * ─────────────────────────────────────────────────────────────────────────────
 * Every step, a car's target speed is the lowest of: its lane's limit, the
 * square-root brake curve to its leader's tail, the same curve to a junction
 * stop line it has not been granted, and zero if a live actor is in the road
 * ahead. It never swerves — a lane is a lane. Actor braking uses an along-lane
 * probe rather than a distance, because a pedestrian on the pavement 5 m to the
 * side is not in the way.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PARKED CARS ARE WORLD, DRIVING CARS ARE NOT
 * ─────────────────────────────────────────────────────────────────────────────
 * A parked car registers two circles in the level's `CollisionGrid`, so the
 * player walks round it and enemies steer round it with the code they already
 * have. A DRIVING car registers nothing: it brakes for actors instead. That
 * asymmetry is deliberate — refreshing 48 moving shapes in a static bucket grid
 * every step would cost more than the whole traffic sim, and being nudged by a
 * moving car is a physics problem this game does not have.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A BAY IS THE LEVEL. A PARKED CAR IS ONLY A BAY THAT CURRENTLY HAS ONE IN IT
 * ─────────────────────────────────────────────────────────────────────────────
 * {@link Traffic#_parkingSlots} is the AUTHORITATIVE layout: every legal bay on
 * the island (~620 on the shipped city), a pure function of `map.roads` and the
 * height sampler with no rng in it at all. What is dynamic is OCCUPANCY —
 * budgeted bays ride a distance ring exactly the way driving cars and guest
 * slots do ({@link TRAFFIC.parkRadius} / {@link TRAFFIC.parkDespawn}), because
 * the camera can only see a ~38 m patch of ground (~100 m zoomed all the way
 * out) and the shipped layout put 40 cars over 320 m of city: measured live,
 * SEVEN of them were within 60 m of the player and the other 33 were simulated,
 * collided and drawn where nobody would ever be.
 *
 * The whole feature stands on one property, and it is not the ring:
 *
 *   ⚠ A BAY'S CAR IS A PURE FUNCTION OF THE BAY, NEVER OF THE DRAW ORDER.
 *
 * Archetype, paint and "does this bay hold a car at all" are LOOKED UP from a
 * hash of (level seed, bay identity), not drawn from a sequential stream. A bay
 * emptied when the player walked away and re-filled when they come back an hour
 * later therefore comes back as the SAME car in the SAME colour — with a
 * sequential draw the city would visibly reshuffle itself behind the player,
 * which is a far worse bug than the one this fixes. It is also why the ring
 * costs the traffic stream nothing: `_trySpawn`'s "always draws the same two
 * numbers" invariant is untouched, because the parking rows draw from no shared
 * stream at all any more.
 *
 * Three things a bay never does: hold an OCCUPANT (only driving slots have
 * seats — see below), retire the car the PLAYER is in, and re-fill itself while
 * its car still exists somewhere else on the island (drive one away, abandon it
 * three districts over, and the bay stays empty until that car is collected —
 * otherwise the player owns a car that is also still parked where they found
 * it).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CARS HAVE PEOPLE IN THEM, AND THIS FILE DOES NOT KNOW WHO
 * ─────────────────────────────────────────────────────────────────────────────
 * Occupants belong to `systems/Population.js`. All the director publishes is a
 * SEAM and an ANCHOR: {@link Traffic#onActivate} / {@link Traffic#onDeactivate}
 * fire as a slot becomes and stops being a live car, and
 * {@link Traffic#getSeatWorld} turns (slot, seat) into a world transform. The
 * hooks exist rather than a poll because a slot can be recycled and refilled
 * inside ONE step, and a poll would silently re-slave the old occupants to the
 * new car. See {@link SEAT} for why the anchor is derived from the spec and not
 * from `carBuild.js#carLayout`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND THE PLAYER CAN TAKE ONE OFF SOMEBODY
 * ─────────────────────────────────────────────────────────────────────────────
 * The director publishes four things a carjack needs and owns none of the
 * choreography (that is `systems/Carjack.js`): {@link requestStop} brings a
 * moving target to a standstill and keeps owning it; {@link pin} takes it out
 * of the despawn ring for the ~1.6 s the sequence runs; {@link nearestJackable}
 * + {@link getDoorWorld} say which car and which door; and {@link takeDriving}
 * converts the live car into a PARKED slot so `systems/Driving.js`'s one and
 * only handover path can be reused verbatim. That last one is why the parked
 * pool is `maxParked` bays PLUS a `TRAFFIC.guestParked` reserve — the bays are
 * all full by design, so without spare slots the first carjack would have
 * nowhere to put the car.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ...AND SOME OF THEM END UP BURNT OUT IN THE ROAD
 * ─────────────────────────────────────────────────────────────────────────────
 * A WRECK IS A PARKED CAR WITH ONE BYTE SET ({@link Traffic#pWreck}). It is
 * still world geometry, still collides, still occupies its slot and still rides
 * the despawn ring — the only thing it stops being is DRIVEABLE
 * ({@link nearestEnterable} skips it). This director owns nothing else about
 * it: the damage, the smoke, the panels hanging off and the decision that a car
 * is dead all belong to `systems/VehicleDamage.js`.
 *
 * A car destroyed while DRIVING goes through {@link wreckDriving}, which is
 * {@link takeDriving} plus that byte — the SAME single handover path a carjack
 * uses, for the same reason.
 *
 * ...and it leaves the world either way it can: on DISTANCE, riding the ring
 * like any other parked car, or on a CLOCK it cannot see, through
 * {@link despawnParked} — the one public door into the collection path, cut for
 * a wreck that has to disappear a fixed time after it burned.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND SOME OF THESE CARS ARE COMING FOR THE PLAYER
 * ─────────────────────────────────────────────────────────────────────────────
 * {@link DRIVE_MODE}`.PURSUIT` is the second drive mode, and it is deliberately
 * SMALL: a pursuit car is still an arc length on a lane, still queues behind
 * its leader, still brakes for pedestrians and still needs a junction claim
 * before it may enter a box. Exactly three things change, and each is one line:
 *
 *  • every speed limit is multiplied by {@link TRAFFIC.pursuitSpeed} — the lane
 *    is still the lane, the car is just driving it harder;
 *  • {@link _chooseNext} stops rolling a die and takes whichever successor ends
 *    NEAREST the goal (`setPursuit`). It is greedy, rng-free and ties break to
 *    the lowest edge id, so a chase is a pure function of the graph and the
 *    goal;
 *  • a car with `priority` set jumps the FIFO queue at a junction. It does NOT
 *    get to ignore the box: {@link _grantClaims}'s grant half is untouched, so
 *    the claim still has to be free AND the exit lane still has to have room.
 *    Blue lights get you to the front of the queue, not through a car.
 *
 * The other two seams the wanted-level response needs are {@link spawnAt}'s
 * options: an ARCHETYPE override (the law's cars are authored, not rolled —
 * see `civArch` and {@link archIndexOf}) and a RESERVED flag, which a
 * population director reads to keep its ambient civilians out of a car that
 * arrives with its own crew.
 *
 * @module engine/traffic/Traffic
 */

/** @typedef {import('./LaneGraph.js').LaneGraph} LaneGraph */
/** @typedef {import('../types.js').Rng} Rng */

/** Driving state. `PLAYER` is a slot handed to `systems/Driving.js`. */
export const CAR_STATE = Object.freeze({
  FREE: 0,
  CRUISE: 1,
  APPROACH: 2,
  CROSS: 3,
  PARKED: 4,
  PLAYER: 5,
});

/**
 * HOW a live car picks its way through the city — stored in
 * {@link Traffic#mode} (a `Uint8Array`, so these are wire values).
 *
 * `AMBIENT` is 0 because a zeroed byte must already mean "ordinary traffic":
 * every existing spawn, every test and every `_activate` lands on it without
 * knowing this enum exists, which is why nothing in `tests/traffic.test.mjs`
 * moved when pursuit arrived.
 * @enum {number}
 */
export const DRIVE_MODE = Object.freeze({
  AMBIENT: 0,
  PURSUIT: 1,
});

/**
 * Every tuning number the director owns. They were settled in the browser on
 * the shipped city; the ones with a comment are the ones that bite.
 */
export const TRAFFIC = Object.freeze({
  maxDriving: 48,
  maxParked: 40,
  /** Civilian archetypes. One build + one bake each, ~9 draws each. */
  archetypes: 10,
  /**
   * The two AUTHORED archetypes — a police cruiser and a SWAT van — appended
   * AFTER the civilian pool and never rolled by the spawn ring (that draw is
   * bounded by `Traffic#civArch`, which is why adding them moved neither a
   * random number nor a shipped test). `CarFleet` builds an archetype lazily on
   * its first `addCar`, so a city that never gets a wanted level pays nothing
   * for them; the first cruiser costs ~9 draws and one build+bake.
   */
  lawArchetypes: Object.freeze(['police', 'swat']),

  // ── longitudinal ──
  accel: 3.2,
  /** Service braking. The follow/stop curves use `curveBrake`, which is lower —
   *  planning against a decel you can actually beat is what leaves headroom. */
  brake: 6.5,
  curveBrake: 3.0,
  /** Nose-to-nose target gap to the car in front, metres. */
  headway: 7,
  /** Standoff: a car brakes to rest this far short of an actor in its lane,
   *  measured from car CENTRE to actor. Found in the browser — as a hard cutoff
   *  rather than a curve it produced a car that noses into the pedestrian at
   *  6 m/s and then stops with its bumper through them. */
  actorStop: 6,
  /** How far ahead an actor is even looked for. Must exceed the stopping
   *  distance from the fastest road (13 m/s at `brake` is ~13 m) or the car
   *  cannot physically stop in time. */
  actorSee: 22,
  /** ...and it only counts if it is within this of the lane centre. A
   *  pedestrian on the pavement is not in the way. */
  actorLateral: 2.4,

  // ── steering ──
  /**
   * HALF-LENGTH of the yaw chord, metres. A car's yaw is the direction of the
   * chord from `s − yawChord` to `s + yawChord` along its path — CENTRED on
   * the car, which is what makes the body visibly rotate about its own middle.
   * The old pure-pursuit aim (`4 + v/2` m ahead) led the tangent at the
   * centre by half the lookahead, and every cornering car read as pivoting
   * about its NOSE (owner playtest round 1).
   */
  yawChord: 2.2,
  /** Radians/second. ~126 deg/s: a 90-degree junction turn takes ~0.7 s. */
  yawRate: 2.2,

  // ── junctions ──
  /** Metres before the link that a yielding car parks its nose. */
  stopGap: 2.0,
  /** A car starts asking for the box this far out. */
  claimDist: 18,
  /** Room the exit lane must have before a claim is granted. */
  exitClear: 9,
  /** Seconds a granted car may sit outside the box before it loses its turn. */
  claimTimeout: 6,
  /** Chance of going straight on where straight on is an option. */
  straightBias: 0.5,

  // ── pursuit ({@link DRIVE_MODE}.PURSUIT — the wanted-level response) ──
  /**
   * Multiplier on every speed limit for a car in pursuit, including the
   * slow-into-the-next-edge curve and the arrival trim. 1.35 is the largest
   * value that still takes a 5.5 m/s turn link cleanly — above it a cruiser
   * carries too much speed into a right-angle fillet and reads as ice, which
   * is the same trap `TURN_SPEED` exists for.
   */
  pursuitSpeed: 1.35,

  // ── signals + lamps ──
  signalLead: 1.5,
  blinkHz: 1.5,
  blinkDuty: 0.55,
  /** `uNight` above which headlights come on. */
  nightOn: 0.35,

  // ── population ──
  spawnMin: 60,
  spawnMax: 90,
  despawn: 120,
  /** Half-angle of the camera-forward cone a car may never appear inside. */
  coneDeg: 35,
  activeMin: 14,
  activeMax: 22,
  /** Lane edges near the focus that count as "full supply" for `activeMax`. */
  supplyFull: 90,
  /** Candidate spawn slots tried per step while under target. */
  tries: 6,
  /** Focus travel that invalidates the cached candidate ring. */
  candidateRefresh: 15,
  /** No car may spawn within this of another on the same lane. */
  spawnClear: 14,
  /**
   * Per-class multiplier on how often the spawn ring's candidate lane gets
   * picked — see `_refreshCandidates`'s weighted candidate list. A class left
   * out defaults to **1** (`TRAFFIC.classWeight[cls] ?? 1`), which is every
   * CITY class and plain `country`: their behaviour is byte-for-byte what it
   * was before the road tiers existed, because the weighting is folded into
   * WHICH candidate `_trySpawn`'s `pick` lands on rather than into an extra
   * roll — `_trySpawn` still draws exactly the same two numbers per attempt it
   * always has, whatever the weights say. See `tests/traffic.test.mjs`'s
   * negative control (all weights at 1 reduces to the old uniform index).
   *
   * `dirt` is deliberately near-zero: a farm track should sit empty almost
   * always, with a truck trundling through the rare exception rather than the
   * rule (paired with the archetype restriction in `_activate` — a dirt spawn
   * that DOES happen only ever rolls a van or a pickup). `highway`/`trunk` sit
   * a little ABOVE 1 — the two roads built to carry more traffic look like it.
   */
  classWeight: Object.freeze({
    highway: 1.3,
    trunk: 1.15,
    dirt: 0.05,
  }),

  // ── carjacking ──
  /** How far from a DRIVING car's front door F reaches. */
  jackReach: 2.5,
  /** Metres of preference the DRIVER'S door gets when both are in range. */
  jackDriverBias: 0.4,

  // ── parking ──
  parkSpacing: 9,
  /** PARKED slots held back from {@link placeParkedCars} for cars the player
   *  takes off the road. A jacked car has to BECOME a parked slot before
   *  `systems/Driving.js` can be handed it, and the placement fills every
   *  budgeted bay — without a reserve the first carjack of a session would
   *  have nowhere to put the car. They are recycled by the despawn ring, so
   *  abandoning cars can never grow the fleet. */
  guestParked: 6,
  /** Metres kept clear at each end of a road's PAVED RECT. The rect already
   *  excludes the junction patch, so this only has to clear the zebra (2 m) and
   *  the stop bar behind it — plus half a car, or the first bay's NOSE parks on
   *  the crossing even though its centre does not. */
  parkJunctionMargin: 6.5,
  /**
   * Kerb inset from the road edge to the parked car's CENTRE-LINE — so the car
   * straddles the kerb, with only `parkKerbInset + halfWidth` of it left on the
   * carriageway.
   *
   * ⚠ It was 1.5, which put the whole car on the tarmac with 0.55 m of road to
   * spare, and owner playtest round 4 called that "too far into the street" —
   * on a 9 m street a parked row left barely a car's width to drive through.
   * At 0.2 the widest shipped body (~1.9 m) has its outer flank 0.75 m OVER the
   * kerb: roughly half the car is up on the pavement, which is the look asked
   * for and gives the carriageway 1.3 m back on both sides.
   *
   * ⚠ AND 0.2 IS A CEILING, NOT A PREFERENCE. `levels/city/props.js` starts its
   * pavement furniture 0.7 m in from the kerb and stands its streetlights at
   * 0.85 m, and neither pass knows a bay layout exists — push the cars further
   * in and they park through lamp posts and benches. Moving them all the way
   * onto the pavement means moving that furniture line first.
   *
   * It stays INSIDE the road edge for a second reason: `heightAt` answers 0.15
   * inside a block rect and 0 on the road, so a centre-line ON the boundary is
   * a coin flip between parking at road level and parking on the kerb, and the
   * two differ by the height of the step.
   */
  parkKerbInset: 0.2,
  /**
   * ONE CITY BAY IN `parkStride` HOLDS A CAR — the density of the parked rows,
   * and the only knob that decides how full a street looks.
   *
   * It is a STRATIFIED gate, not a probability: the bays are walked in blocks
   * of `parkStride` and a hash of (seed, block) picks WHICH one of the block is
   * occupied. That matters twice over. The count is then exact rather than
   * binomial — 560 city bays give 112 cars' worth of layout on every seed, so
   * "are there at least `maxParked` bays in range" can never fail on an unlucky
   * hash — and the spacing still varies from 1 to 2·stride−1 bays, so the row
   * reads as parking rather than as a comb.
   *
   * 5 puts a car every ~45 m of kerb. With {@link TRAFFIC.parkRadius} at 120 m
   * that is 36–44 occupied bays around a downtown focus against a `maxParked`
   * of 40 — deliberately just over, so the budget is the thing that binds and
   * it binds at the FAR edge of the ring, off screen. The shipped level before
   * the ring was 1 bay in 14, spread over the whole island.
   */
  parkStride: 5,
  /**
   * The same gate on the country VERGES, and it is looser because they are a
   * different kind of place.
   *
   * ⚠ The verge is also a different place from where these cars used to stand.
   * A country road is a 7 m ribbon with NO paved shoulder, so the old
   * `width/2 − parkKerbInset` inset — correct for a city kerb — put every rural
   * car in the middle of the travel lane, where it read as (and behaved as)
   * stuck traffic (owner playtest round 1). They park on the GRASS now
   * ({@link TRAFFIC.parkVergeClear}), and a handful of them is the point: a
   * lay-by every few hundred metres is scenery, a row of them is a car park.
   * 8 over 62 verge bays is 7 lay-bys on the whole island.
   */
  parkCountryStride: 8,
  /**
   * A SECOND, INDEPENDENT thinning applied only to DIRT verge bays that the
   * stratified `parkCountryStride` gate already turned on — the fraction of
   * those that survive. `parkCountryStride` alone (1 in 8) is a normal country
   * lay-by rate; a dirt track wants rarer than that, so this multiplies it
   * down further to roughly 1 in 27 rather than adding a whole second stride
   * table that has to be kept in step with the first. Every non-dirt bay never
   * calls this at all, so their occupancy is exactly what `parkCountryStride`
   * alone has always produced.
   */
  parkDirtKeep: 0.3,
  /**
   * THE PARKED RING. A budgeted bay within `parkRadius` of the focus gets a
   * car; a car further than `parkDespawn` from the focus is collected. The gap
   * between them is hysteresis — without it a bay on the boundary would build
   * and delete a car every few metres of walking.
   *
   * ⚠ THE ZOOM-OUT CASE IS WHAT SETS THE NUMBER. At the shipped camera (30 deg
   * lens, 52 deg pitch, 34 m out) the visible ground is a ~38 m square, but
   * `CameraRig.maxDistance` is 90 m and at full zoom-out it is ~100 m across —
   * a half-diagonal of ~71 m. 120 m clears that by half as much again in every
   * direction, so a car can never be seen appearing; 150 m is the same margin
   * on the way out. They are deliberately WIDER than the driving ring's 60–90 /
   * 120, because a parked car is furniture the player can walk back to and a
   * driving car is traffic that is allowed to have gone.
   */
  parkRadius: 120,
  parkDespawn: 150,
  /** Focus travel that re-runs the parked ring. Cheap (~620 distance tests),
   *  but there is nothing to see in doing it per frame. */
  parkRefresh: 8,
  /**
   * Metres between the country ribbon's EDGE and the verge bay's centre line.
   * At 1.4 the widest shipped body (~2 m) leaves ~0.4 m of grass between its
   * flank and the tarmac, so nothing overhangs the lane a car has to drive.
   * {@link Traffic#_parkingSlots} then MEASURES the finished bay against the
   * whole polyline and drops anything under `width/2 + parkVergeMin`, because
   * on the inside of a bend the neighbouring segment is closer than the offset.
   */
  parkVergeClear: 1.4,
  /** The measured floor for that check, past the ribbon's half-width. */
  parkVergeMin: 1.0,
  /**
   * A verge bay has to stand this far above `map.seaLevel` — a car half-sunk in
   * the sand band at the waterline is worse than no car — and the ground under
   * it may not tilt more than `parkVergeGrade` (rise over run), sampled
   * `parkVergeProbe` metres either side in both axes. The road corridor is
   * carved flat to `country.corridor` (6 m) and feathered to `shoulder` (11 m),
   * so the strip a bay lands in is usually flat by construction; these two are
   * what stop the exception — a road cut into a hillside — parking a car in mid
   * air on the cut face.
   */
  parkVergeDry: 0.5,
  parkVergeGrade: 0.18,
  parkVergeProbe: 1.5,
});

const DEG = Math.PI / 180;

/**
 * The two-circle footprint a PARKED car puts in the collision grid: centres at
 * `±L·at` from the middle, radius `W/2 + fat`. See `_setParkedColliders` for
 * why it is not the axle line.
 */
export const PARK_CIRCLE = Object.freeze({ at: 0.22, fat: 0.25 });

/**
 * WHERE AN OCCUPANT SITS, in car-local metres — the anchor a population
 * director slaves a seated actor to (`getSeatWorld`).
 *
 * Derived from the SPEC, never from `carBuild.js#carLayout`: that function
 * imports three.js and this file must not (see the header). The two agree well
 * enough for an anchor nobody draws, and `tests/factions.test.mjs` pins the seat inside
 * the cabin's fore-aft span for every shipped body type.
 *
 * Three numbers, and each is a decision:
 *  • `backFromAxle` puts the hip a fraction of a wheelbase behind the front
 *    axle. It is deliberately FORWARD of a real driving position (18 %, not the
 *    30 % a real car uses) and that is a CAMERA decision, not an anatomical
 *    one: at the game's 52-degree pitch the roof panel projects over everything
 *    behind the windscreen's leading edge, so a correctly-seated driver is a
 *    driver nobody can see. Sat under the glass instead, the head and shoulders
 *    read through the screen from every angle the camera can take. `cowlClear`
 *    then stops the same rule from putting a van's driver on the bumper.
 *  • `halfWidth` is a fraction of the body width, so a wide SUV seats its two
 *    occupants further apart than a hatchback does.
 *  • `lift` is NOT a seat height. It is where the occupant's FEET go — the
 *    footwell — and it exists so a slot that is shot in a car dies at a
 *    plausible height. Nobody draws a body there (see the warning below).
 *  • `rearBack` pushes the REAR pair (seats 2 and 3) back along −X from the
 *    front pair, as a fraction of the wheelbase. Rear seats only exist on a
 *    2-row body ({@link Traffic#seatCount}) — a pickup has no rear door to
 *    bail out of.
 *
 * ⚠ AND NOTHING IS DRAWN AT THIS ANCHOR ANY MORE (owner decision, playtest
 * round 2). An occupant used to be a full standing chibi sunk into the car —
 * tinted near-black after round 1 — and it never worked at any yaw the camera
 * could take: the sprite is CAMERA-FACING and the cabin is not, so a shape
 * sized to fit the glass head-on swings out through the door as the car turns.
 * A small solid HEAD at the beltline was tried in its place and clipped
 * through the windscreen, which is the same failure with less of it. So a car
 * is drawn EMPTY: the seat still positions a live actor (it is shootable, it
 * can be dragged out, it dies at a plausible place) and the renderers simply
 * skip it — `EnemyManager#inCabin`. Anything that ever puts a body back in a
 * cabin has to solve the cabin INTERIOR first, not the anchor.
 */
export const SEAT = Object.freeze({
  backFromAxle: 0.18,
  cowlClear: 0.18,
  halfWidth: 0.22,
  lift: 0.14,
  rearBack: 0.34,
});

// ─────────────────────────────────────────────────────────────────────────────

export class Traffic {
  /**
   * @param {Object} o
   * @param {LaneGraph} o.graph
   * @param {import('../types.js').LevelMapData|null} [o.map] Needed for the
   *   PARKED rows — they follow the lattice's paved rects, not the lane graph
   *   (a kerb is a property of the road, not of the carriageway).
   * @param {any} [o.fleet] A `CarFleet`, or null to simulate headless.
   * @param {boolean} [o.ownsFleet=true] Dispose the fleet with the director.
   * @param {any} [o.collision] `CollisionGrid` — parked cars register circles.
   * @param {(x: number, z: number) => number} [o.heightAt]
   * @param {() => number} [o.getNight] Live `uNight`, for the headlights.
   * @param {string|number} [o.seed='traffic']
   * @param {boolean} [o.autoPopulate=true] Run the spawn/despawn ring. Off
   *   leaves the pool entirely to {@link spawnAt} — scripted scenes and tests.
   * @param {number} [o.maxDriving] @param {number} [o.maxParked]
   */
  constructor(o) {
    const {
      graph,
      map = null,
      fleet = null,
      ownsFleet = true,
      collision = null,
      heightAt = null,
      getNight = null,
      seed = 'traffic',
      autoPopulate = true,
      maxDriving = TRAFFIC.maxDriving,
      maxParked = TRAFFIC.maxParked,
    } = o ?? {};

    if (!graph) throw new Error('Traffic: a LaneGraph is required');

    /** @type {LaneGraph} */
    this.graph = graph;
    this.map = map;
    this.fleet = fleet;
    this._ownsFleet = ownsFleet;
    this.collision = collision;
    this.heightAt = typeof heightAt === 'function' ? heightAt : () => 0;
    this._getNight = getNight;
    this.seed = seed;
    this.autoPopulate = autoPopulate !== false;

    /** Caller-side clock — the fleet shader has none (see `CarFleet.js`). */
    this.clock = 0;
    this._ticket = 1;
    this._disposed = false;

    // ── archetypes ───────────────────────────────────────────────────────────
    // One stream for everything structural about the fleet, a second for the
    // turn choices, so adding a body type cannot reshuffle who turns where.
    const rng = streamRng(seed, 'traffic');
    this._rng = rng;
    this._turnRoot = streamRng(seed, 'traffic.turns');

    /** @type {string[]} */
    this.archKeys = [];
    /** @type {any[]} */
    this.archSpecs = [];
    /** @type {Array<{L: number, W: number, wheelR: number, fa: number, ra: number, wheelbase: number}>} */
    this.archChassis = [];
    for (let i = 0; i < TRAFFIC.archetypes; i++) {
      // The first six are one of each body type: "a mixed pool" must be a
      // property of the build, not of a lucky seed.
      const type = i < BODY_TYPES.length ? BODY_TYPES[i] : BODY_TYPES[(rng() * BODY_TYPES.length) | 0];
      const s = (rng() * 0xffffffff) >>> 0;
      const key = archetypeKey(type, s);
      const spec = specFromKey(key);
      this.archKeys.push(key);
      this.archSpecs.push(spec);
      this.archChassis.push(chassisOf(spec));
    }
    /**
     * How many of {@link archKeys} the spawn ring and the parking rows may
     * ROLL. Everything past it is authored (the law's cars), reachable only by
     * a caller that names it through {@link spawnAt}'s `arch` option.
     *
     * ⚠ This is the number the random draw is bounded by, and it is exactly
     * what `archKeys.length` was before the law was appended. That is the whole
     * reason two extra archetypes did not reshuffle a single civilian car.
     * @type {number}
     */
    this.civArch = this.archKeys.length;
    /**
     * Civilian archetype indices whose body reads as a truck or van — `'van'`
     * and `'pickup'` in {@link BODY_TYPES}. The ONLY pool a DIRT-lane spawn
     * ({@link _activate}) or a dirt verge bay ({@link _buildBays}) may draw
     * from: a dusty farm track gets an occasional truck trundling through, not
     * a saloon. `BODY_TYPES[4]`/`[5]` (van, pickup) are GUARANTEED present by
     * the "one of each body type" rule just above whenever
     * `TRAFFIC.archetypes >= BODY_TYPES.length`, so this is never empty in the
     * shipped config; it is a SCAN rather than a hard-coded `[4, 5]` so it
     * stays correct if that ever changes.
     * @type {number[]}
     */
    this._truckArch = [];
    for (let i = 0; i < this.civArch; i++) {
      const t = this.archSpecs[i]?.type;
      if (t === 'van' || t === 'pickup') this._truckArch.push(i);
    }
    /** @type {Object<string, number>} Archetype index per law kind. */
    this.lawArch = {};
    // A DEDICATED stream (rule 4): the law's seeds must not be drawn from the
    // fleet stream, or adding a police archetype would re-roll every civilian
    // body in the city.
    const lawRng = streamRng(seed, 'traffic.law');
    for (const kind of TRAFFIC.lawArchetypes) {
      const key = archetypeKey(kind, (lawRng() * 0xffffffff) >>> 0);
      const spec = specFromKey(key);
      this.lawArch[kind] = this.archKeys.length;
      this.archKeys.push(key);
      this.archSpecs.push(spec);
      this.archChassis.push(chassisOf(spec));
    }

    // ── driving pool ─────────────────────────────────────────────────────────
    const N = (this.maxDriving = Math.max(1, maxDriving | 0));
    this.state = new Uint8Array(N);
    this.edge = new Int32Array(N).fill(-1);
    this.nextEdge = new Int32Array(N).fill(-1);
    this.s = new Float64Array(N);
    this.v = new Float32Array(N);
    this.x = new Float32Array(N);
    this.y = new Float32Array(N);
    this.z = new Float32Array(N);
    this.yaw = new Float32Array(N);
    this.steer = new Float32Array(N);
    this.claim = new Int32Array(N).fill(-1);
    this.claimT = new Float32Array(N);
    this.want = new Int32Array(N).fill(-1);
    this.ticket = new Int32Array(N);
    this.handle = new Int32Array(N).fill(-1);
    this.arch = new Uint8Array(N);
    /** Index into `PAINTS`. Kept so {@link takeDriving} can hand a jacked car's
     *  colour to the parked slot it becomes — the fleet is write-only. */
    this.paint = new Uint8Array(N);
    this.hold = new Uint8Array(N);
    /** 1 = the despawn ring may not retire this car. See {@link pin}. */
    this.pinned = new Uint8Array(N);
    this.decel = new Uint8Array(N);
    this.sig = new Int8Array(N);
    /** @type {Uint8Array} {@link DRIVE_MODE}. Zeroed = ordinary traffic. */
    this.mode = new Uint8Array(N);
    /** Pursuit goal, world XZ. Only read while `mode` is PURSUIT. */
    this.goalX = new Float32Array(N);
    this.goalZ = new Float32Array(N);
    /**
     * @type {Uint8Array} 1 = this car jumps the FIFO junction queue. It still
     * cannot enter an occupied box or block an exit lane — see the header.
     */
    this.priority = new Uint8Array(N);
    /**
     * @type {Uint8Array} 1 = somebody else is filling this car's seats. The
     * population director reads it in its `onActivate` and puts nobody aboard,
     * which is how a police car arrives with police in it rather than with the
     * two civilians the ambient rule would have seated. Cleared by
     * {@link _deactivate}, so it can never leak into the slot's next tenant.
     */
    this.reserved = new Uint8Array(N);
    /** @type {Array<Rng|null>} Per-car turn chooser, seeded on spawn. */
    this.turnRng = new Array(N).fill(null);
    this.top = 0;
    this.driving = 0;

    // ── parked pool ──────────────────────────────────────────────────────────
    // `maxParked` is the PLACEMENT budget (what `placeParkedCars` lays on the
    // kerbs); `parkedCap` is the array length, which adds the guest reserve a
    // carjack needs. Every loop over the pool bounds on `parkedCap`.
    const P = (this.maxParked = Math.max(0, maxParked | 0));
    const PC = (this.parkedCap = P + TRAFFIC.guestParked);
    this.pState = new Uint8Array(PC);
    this.pX = new Float32Array(PC);
    this.pY = new Float32Array(PC);
    this.pZ = new Float32Array(PC);
    this.pYaw = new Float32Array(PC);
    this.pHandle = new Int32Array(PC).fill(-1);
    this.pArch = new Uint8Array(PC);
    /** Index into `PAINTS` — the bay's colour, and the whole point of pinning
     *  it: a bay re-occupied an hour later has to come back the same car. */
    this.pPaint = new Uint8Array(PC);
    this.pCol0 = new Int32Array(PC).fill(-1);
    this.pCol1 = new Int32Array(PC).fill(-1);
    /**
     * @type {Uint8Array} 1 = this parked car is a WRECK — burnt out, still
     * standing, still world geometry, and never enterable again
     * ({@link nearestEnterable} skips it). It lives on the PARKED pool and only
     * there, because that is the only place a dead car can be: a driving one
     * goes through {@link wreckDriving}, which retires the driving slot the
     * ordinary way and marks the parked slot it becomes.
     *
     * ⚠ It is a property of the CAR, not of the bay, so it must be cleared
     * wherever a slot changes hands — {@link _initParked} on the way in and
     * {@link _freeParked} on the way out. Miss either and a recycled bay comes
     * back holding a car that looks fine and cannot be driven, which is a bug
     * with no symptom until a player walks up to it. `takeParked` /
     * `returnParked` deliberately do NOT clear it: that pair is the same car
     * being handed back and forth.
     */
    this.pWreck = new Uint8Array(PC);
    /** @type {Array<{x: number, z: number}|null>} Driver-door offset, car-local. */
    this.pDoor = new Array(PC).fill(null);
    this.parked = 0;

    // ── the parked ring: bays, and which slot is standing in which bay ───────
    // Built once by `placeParkedCars`; null until then, which is what makes a
    // director that never calls it (every dev scene) pay nothing.
    /** @type {Float32Array|null} */
    this._bayX = null;
    /** @type {Float32Array|null} */
    this._bayZ = null;
    /** @type {Float32Array|null} */
    this._bayYaw = null;
    /** @type {Uint8Array|null} Archetype, hashed from the bay — never drawn. */
    this._bayArch = null;
    /** @type {Uint8Array|null} `PAINTS` index, likewise. */
    this._bayPaint = null;
    /** @type {Uint8Array|null} 1 = this bay is one of the occupied ones. */
    this._bayGate = null;
    /** @type {Uint8Array|null} 1 = a country VERGE bay rather than a city kerb. */
    this._bayCountry = null;
    /**
     * @type {Int32Array|null} Parked slot standing in each bay, or −1.
     * THE ANTI-DUPLICATE RULE lives here: the binding survives the player
     * driving the car away, so the bay stays empty for as long as its car
     * exists anywhere. See {@link _repark}.
     */
    this._baySlot = null;
    /** Reverse of `_baySlot`. Guest slots are never bound. */
    this._slotBay = new Int32Array(PC).fill(-1);
    /** @type {Int32Array|null} Fill scratch — allocated with the roster. */
    this._bayCand = null;
    /** @type {Float64Array|null} */
    this._bayCandD = null;
    this._bayN = 0;
    /** Occupiable bays ({@link _bayGate} set) — the layout's own budget. */
    this._bayCars = 0;
    /** Where the ring last ran. NaN until the first pass. */
    this._parkX = NaN;
    this._parkZ = NaN;
    /** Reported by {@link stats}, so the ring's churn cannot rot silently. */
    this.parkPlaced = 0;
    this.parkCulled = 0;

    // ── junction claims ──────────────────────────────────────────────────────
    const K = graph.nodes.length;
    this.claimOwner = new Int32Array(K).fill(-1);
    this._bestCar = new Int32Array(K).fill(-1);
    this._bestTicket = new Int32Array(K);
    this._bestStamp = new Int32Array(K).fill(-1);
    /** Priority of the current front-runner per node — see {@link priority}. */
    this._bestPri = new Int32Array(K);
    this._stamp = 0;

    // ── spawn ring cache ─────────────────────────────────────────────────────
    this._candidates = new Int32Array(Math.max(1, graph.laneEdges.length));
    /** Cumulative {@link TRAFFIC.classWeight} over `_candidates[0.._candN)`,
     *  for {@link _pickCandidate}'s weighted binary search. */
    this._candCum = new Float64Array(Math.max(1, graph.laneEdges.length));
    /** Total weight in `_candCum` — `_candCum[_candN - 1]` once refreshed. */
    this._candWSum = 0;
    this._candN = 0;
    this._supply = 0;
    this._candX = NaN;
    this._candZ = NaN;

    // ── scratch (allocated ONCE — `update` must not litter) ───────────────────
    this._sample = { x: 0, z: 0, dx: 1, dz: 0 };
    this._sample2 = { x: 0, z: 0, dx: 1, dz: 0 };
    this._sample3 = { x: 0, z: 0, dx: 1, dz: 0 };
    this._focus = { x: 0, z: 0, yaw: 0 };
    /** Actors driving cars cost speed: the player on foot, and the player car. */
    this._actors = [
      { x: 0, z: 0, r: 0.5, on: false },
      { x: 0, z: 0, r: 1.2, on: false },
    ];
    this._lampState = { head: 0, brake: 0, sigL: 0, sigR: 0 };
    this._seatOut = { x: 0, y: 0, z: 0, yaw: 0 };
    this._seatLocal = { x: 0, z: 0 };

    /**
     * THE POPULATION SEAM. Fired the moment a driving slot becomes (or stops
     * being) a live AI car, so a director can put people in it and take them
     * out again without polling — which matters because a slot can be recycled
     * and refilled inside ONE step, and a poll would slave the old occupants to
     * the new car.
     *
     * Null by default: the director is optional and `tests/traffic.test.mjs`
     * never sets one.
     * @type {((i: number) => void)|null}
     */
    this.onActivate = null;
    /** @type {((i: number) => void)|null} Fired BEFORE the slot is cleared. */
    this.onDeactivate = null;

    /** Lane-edge midpoints, for the spawn ring. Built once. */
    const lanes = graph.laneEdges;
    this._midX = new Float32Array(lanes.length);
    this._midZ = new Float32Array(lanes.length);
    for (let i = 0; i < lanes.length; i++) {
      const e = graph.edges[lanes[i]];
      graph.sampleAt(e.id, e.length * 0.5, this._sample);
      this._midX[i] = this._sample.x;
      this._midZ[i] = this._sample.z;
    }
  }

  // ── actors the traffic must not run over ──────────────────────────────────

  /**
   * Tells the director where the on-foot player is. Cars brake for it; they
   * never swerve.
   * @param {number} x @param {number} z @param {boolean} on
   */
  setPedestrian(x, z, on) {
    const a = this._actors[0];
    a.x = x;
    a.z = z;
    a.on = !!on;
  }

  /**
   * The PLAYER's car, as an obstacle. It is not in the SoA — `Driving.js` owns
   * it — so the director has to be told.
   * @param {number} x @param {number} z @param {boolean} on
   */
  setPlayerCar(x, z, on) {
    const a = this._actors[1];
    a.x = x;
    a.z = z;
    a.on = !!on;
  }

  /**
   * Where the population ring is centred and which way the camera looks.
   * @param {number} x @param {number} z @param {number} camYaw
   */
  setFocus(x, z, camYaw) {
    this._focus.x = x;
    this._focus.z = z;
    this._focus.yaw = camYaw;
  }

  // ── the seam the carjack task calls ───────────────────────────────────────

  /**
   * Forces a driving car to brake to a stop and hold it. THE CARJACK SEAM: the
   * approach animation needs the target to stop being a moving object before
   * the player can be attached to it, and the director needs to keep owning it
   * (queue position, junction claim) until the moment it is taken.
   *
   * Idempotent; `releaseStop` resumes.
   * @param {number} i Driving slot.
   * @returns {boolean} False when the slot is not a live driving car.
   */
  requestStop(i) {
    if (!this._isDriving(i)) return false;
    this.hold[i] = 1;
    return true;
  }

  /** @param {number} i @returns {boolean} */
  releaseStop(i) {
    if (!this._isDriving(i)) return false;
    this.hold[i] = 0;
    return true;
  }

  /** @param {number} i @returns {boolean} True once the held car has stopped. */
  isStopped(i) {
    return this._isDriving(i) && this.v[i] < 0.05;
  }

  /**
   * Suppresses the DESPAWN RING for one car. The other half of the carjack
   * seam: a sequence lasts ~1.6 s, and in that time the player can walk the
   * focus far enough for {@link _recycle} to retire the very car being
   * jacked — which would strand its driver in mid-drag and leave the sequence
   * holding a slot that has already been handed to somebody else.
   *
   * It does NOT stop the car (that is {@link requestStop}) and it does not
   * survive the slot: {@link _activate} clears it, so a pin can never leak
   * into the slot's next tenant.
   *
   * @param {number} i Driving slot.
   * @param {boolean} on
   * @returns {boolean} False when the slot index is out of range.
   */
  pin(i, on) {
    if (!(i >= 0 && i < this.maxDriving)) return false;
    this.pinned[i] = on ? 1 : 0;
    return true;
  }

  /** @param {number} i @returns {boolean} */
  isPinned(i) {
    return i >= 0 && i < this.maxDriving && this.pinned[i] === 1;
  }

  // ── pursuit: the seam the wanted-level response drives ────────────────────

  /**
   * Puts a live car into {@link DRIVE_MODE}`.PURSUIT` and aims it at a world
   * point. Idempotent, and cheap enough to call every step — the CALLER owns
   * the repath cadence, because "how often the police re-read where you are"
   * is a difficulty knob and not a property of the road network.
   *
   * ⚠ It does NOT tear up a plan in flight. A car that has already been granted
   * a junction box keeps its claim and finishes crossing; the new goal is read
   * by the NEXT {@link _chooseNext}. Re-planning mid-box would strand a claim
   * for a frame and let two cars into one crossing, which is the single thing
   * the whole junction protocol exists to prevent.
   *
   * @param {number} i Driving slot.
   * @param {number} x @param {number} z The goal (usually the player).
   * @param {boolean} [priority=false] May it jump the FIFO queue? See the header.
   * @returns {boolean} False when the slot is not a live driving car.
   */
  setPursuit(i, x, z, priority = false) {
    if (!this._isDriving(i)) return false;
    this.mode[i] = DRIVE_MODE.PURSUIT;
    this.goalX[i] = x;
    this.goalZ[i] = z;
    this.priority[i] = priority ? 1 : 0;
    return true;
  }

  /**
   * Back to ordinary traffic: the speed boost, the goal-seeking turns and the
   * queue jump all go at once. THE RETIRE PATH — a response car that is let go
   * simply rejoins the city and is retired by the despawn ring like any other.
   * @param {number} i @returns {boolean}
   */
  clearPursuit(i) {
    if (!(i >= 0 && i < this.maxDriving)) return false;
    this.mode[i] = DRIVE_MODE.AMBIENT;
    this.priority[i] = 0;
    return true;
  }

  /** @param {number} i @returns {boolean} */
  isPursuing(i) {
    return this._isDriving(i) && this.mode[i] === DRIVE_MODE.PURSUIT;
  }

  /**
   * @param {number} i @returns {boolean} Somebody else owns this car's seats —
   *   see {@link reserved}. Safe on any index, because the population director
   *   calls it optionally (`traffic.isReserved?.(car)`).
   */
  isReserved(i) {
    return i >= 0 && i < this.maxDriving && this.reserved[i] === 1;
  }

  /**
   * @param {string} kind A {@link TRAFFIC.lawArchetypes} entry (`'police'`,
   *   `'swat'`).
   * @returns {number} Its archetype index, for {@link spawnAt}'s `arch` option;
   *   −1 when this director has no such archetype.
   */
  archIndexOf(kind) {
    const i = this.lawArch[kind];
    return i === undefined ? -1 : i;
  }

  /** @param {number} i @returns {number} Speed-limit multiplier for this slot. */
  _speedScale(i) {
    return this.mode[i] === DRIVE_MODE.PURSUIT ? TRAFFIC.pursuitSpeed : 1;
  }

  // ── seats: the anchors an occupant is slaved to ───────────────────────────

  /**
   * Seat position in CAR-LOCAL metres (+X is the nose, −Z is the driver's
   * side — the vehicle stack's convention, `carSpec.js`).
   *
   * @param {number} arch Archetype index.
   * @param {number} seat 0 = driver, 1 = front passenger, 2 = rear left
   *   (driver's side), 3 = rear right. Rear seats derive from the front pair,
   *   pushed back {@link SEAT.rearBack} of a wheelbase along −X.
   * @param {{x: number, z: number}} out REUSED.
   * @returns {{x: number, z: number}} `out`.
   */
  seatLocal(arch, seat, out) {
    const spec = this.archSpecs[arch] ?? this.archSpecs[0];
    const ch = this.archChassis[arch] ?? this.archChassis[0];
    const cowl = spec.L / 2 - spec.hoodLen;
    let x = ch.fa - ch.wheelbase * SEAT.backFromAxle;
    // Never in front of the windscreen base — a van's bonnet is 0.5 m long and
    // the axle rule alone would seat its driver on the bumper.
    const limit = cowl - SEAT.cowlClear;
    if (x > limit) x = limit;
    if (seat >= 2) x -= ch.wheelbase * SEAT.rearBack;
    out.x = x;
    out.z = (seat % 2 === 0 ? -1 : 1) * spec.W * SEAT.halfWidth;
    return out;
  }

  /**
   * How many seats this archetype's cabin holds: 4 for a 2-row body (the ones
   * that ship rear doors), 2 for everything else. The population director asks
   * before it puts anyone in a rear seat.
   * @param {number} arch Archetype index.
   * @returns {number} 2 or 4.
   */
  seatCount(arch) {
    return (this.archSpecs[arch] ?? this.archSpecs[0])?.rows === 2 ? 4 : 2;
  }

  /**
   * A DRIVING car's seat anchor in world space, ready to hand straight to
   * `EnemyManager#placeSeated`.
   *
   * Driving cars only: parked ones are empty by design (a parked car with a
   * driver in it is a car that should be moving), and the player's car is
   * `systems/Driving.js`'s business.
   *
   * @param {number} i Driving slot.
   * @param {number} seat 0 = driver, 1 = front passenger, 2/3 = rear pair
   *   (see {@link seatLocal}; ask {@link seatCount} whether they exist).
   * @param {{x: number, y: number, z: number, yaw: number}} [out] REUSED
   *   scratch; omit to use the director's own.
   * @returns {{x: number, y: number, z: number, yaw: number}|null} Null when
   *   the slot is not a live driving car.
   */
  getSeatWorld(i, seat, out) {
    if (!this._isDriving(i)) return null;
    const o = out ?? this._seatOut;
    const l = this.seatLocal(this.arch[i], seat, this._seatLocal);
    const yaw = this.yaw[i];
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    // Local (x,z) -> world: Ry(yaw) maps +X to (cos, −sin) and +Z to (sin, cos).
    o.x = this.x[i] + l.x * c + l.z * sn;
    o.z = this.z[i] - l.x * sn + l.z * c;
    o.y = this.y[i] + SEAT.lift;
    // The occupant's facing is the CAR's heading, expressed in the actor
    // convention (`atan2(dx, dz)` from +Z) rather than the vehicle one.
    o.yaw = Math.atan2(c, -sn);
    return o;
  }

  // ── doors: where a body crosses the sill ──────────────────────────────────

  /**
   * FRONT-DOOR handle position in CAR-LOCAL metres. `side` 0 is the driver's
   * (−Z — the vehicle stack's convention, `carSpec.js`), 1 the front
   * passenger's.
   *
   * Spec-derived, like {@link seatLocal} and for the same reason: this file
   * must not import `carBuild.js#carLayout`, which imports three.js. A PARKED
   * car refines the `x` from the real layout when a fleet is present
   * ({@link _doorOf}); a driving one never needs to, because the anchor only
   * has to be within a hand's breadth of the door for a 2.5 m reach test.
   *
   * @param {number} arch Archetype index.
   * @param {number} side 0 = driver, 1 = front passenger.
   * @param {{x: number, z: number}} out REUSED.
   * @returns {{x: number, z: number}} `out`.
   */
  doorLocal(arch, side, out) {
    const spec = this.archSpecs[arch] ?? this.archSpecs[0];
    out.x = spec.L * 0.12;
    out.z = (side === 0 ? -1 : 1) * (spec.W / 2 + 0.35);
    return out;
  }

  /**
   * A DRIVING car's front-door anchor in world space — where the player has to
   * stand to open it, and where a dragged occupant crosses the sill.
   * @param {number} i Driving slot.
   * @param {number} side 0 = driver, 1 = front passenger.
   * @param {{x: number, y: number, z: number, yaw: number}} [out] REUSED.
   * @returns {{x: number, y: number, z: number, yaw: number}|null}
   */
  getDoorWorld(i, side, out) {
    if (!this._isDriving(i)) return null;
    const o = out ?? this._seatOut;
    const l = this.doorLocal(this.arch[i], side, this._seatLocal);
    const yaw = this.yaw[i];
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    o.x = this.x[i] + l.x * c + l.z * sn;
    o.z = this.z[i] - l.x * sn + l.z * c;
    o.y = this.y[i];
    o.yaw = Math.atan2(c, -sn);
    return o;
  }

  /**
   * A PARKED car's DRIVER-door anchor in world space — the same point
   * {@link nearestEnterable} measures against, published so the entry
   * animation can walk the player to it.
   * @param {number} i Parked slot.
   * @param {{x: number, y: number, z: number, yaw: number}} [out] REUSED.
   * @returns {{x: number, y: number, z: number, yaw: number}|null}
   */
  getParkedDoorWorld(i, out) {
    if (!(i >= 0 && i < this.parkedCap) || this.pState[i] === CAR_STATE.FREE) return null;
    const door = this.pDoor[i];
    if (!door) return null;
    const o = out ?? this._seatOut;
    const yaw = this.pYaw[i];
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    o.x = this.pX[i] + door.x * c + door.z * sn;
    o.z = this.pZ[i] - door.x * sn + door.z * c;
    o.y = this.pY[i];
    o.yaw = Math.atan2(c, -sn);
    return o;
  }

  /** @param {number} i @returns {number} Fleet handle of a PARKED slot, or −1. */
  parkedHandle(i) {
    return i >= 0 && i < this.parkedCap ? this.pHandle[i] : -1;
  }

  /** @param {number} i @returns {number} Fleet handle of a DRIVING slot, or −1. */
  drivingHandle(i) {
    return i >= 0 && i < this.maxDriving ? this.handle[i] : -1;
  }

  // ── parked-car handover (used by `systems/Driving.js`) ────────────────────

  /**
   * Nearest ENTERABLE car: a parked one whose driver door is within `maxDist`.
   * Driving cars are excluded on purpose — taking one of those is carjacking,
   * which goes through {@link nearestJackable} and {@link takeDriving}.
   *
   * ⚠ AND A WRECK IS NOT ENTERABLE. You cannot drive a burnt-out car, and the
   * refusal belongs HERE rather than in `systems/Driving.js`: `enterCar` takes
   * a slot index from whoever hands it one (the carjack sequence, a test, a
   * future mission script), so a gate at the call site is a gate one caller
   * will forget. This is also the ONLY place the rule has to live — a wreck can
   * never be in the driving pool, because {@link wreckDriving} retires that
   * slot in the same call that marks the parked one.
   *
   * @param {number} x @param {number} z @param {number} [maxDist=2.2]
   * @returns {number} Parked slot, or −1.
   */
  nearestEnterable(x, z, maxDist = 2.2) {
    let best = -1;
    let bestD = maxDist;
    for (let i = 0; i < this.parkedCap; i++) {
      if (this.pState[i] !== CAR_STATE.PARKED || this.pWreck[i]) continue;
      const door = this.pDoor[i];
      const c = Math.cos(this.pYaw[i]);
      const sn = Math.sin(this.pYaw[i]);
      // Local (dx,dz) -> world: Ry(yaw) maps +X to (cos, -sin) and +Z to (sin, cos).
      const wx = this.pX[i] + door.x * c + door.z * sn;
      const wz = this.pZ[i] - door.x * sn + door.z * c;
      const d = Math.hypot(x - wx, z - wz);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /**
   * Nearest CARJACKABLE car: a live AI one with a front door within `maxDist`,
   * and which side of it the caller is standing on. The mirror of
   * {@link nearestEnterable} for the moving half of the fleet.
   *
   * The DRIVER'S side wins by {@link TRAFFIC.jackDriverBias} metres when both
   * doors are in range: standing off a car's nose is geometrically ambiguous,
   * and "F took the driver's door" is the answer a player expects. The reach
   * test itself is against the RAW distance, so the bias can never pull a door
   * the player is not actually beside into range.
   *
   * ⚠ A WRECK IS NOT JACKABLE EITHER, and it needs no test of its own to be
   * true. `_isDriving` is the gate, and a wreck is never a driving slot: the
   * only way a live car becomes one is {@link wreckDriving}, which hands the
   * slot to the PARKED pool through {@link takeDriving} in the same call. So
   * the burnt-out shell in the road answers −1 from this AND −1 from
   * {@link nearestEnterable}, by two different mechanisms, and F beside it does
   * nothing either way. Do not "fix" the asymmetry by giving the driving pool a
   * wreck byte of its own: nothing would ever set it, and the day something did
   * there would be two ways for a car to be dead.
   *
   * @param {number} x @param {number} z
   * @param {number} [maxDist=TRAFFIC.jackReach]
   * @param {{side: number, dist: number}} [out] REUSED. `side` 0 = driver,
   *   1 = front passenger; `dist` the raw door distance in metres.
   * @returns {number} Driving slot, or −1.
   */
  nearestJackable(x, z, maxDist = TRAFFIC.jackReach, out = null) {
    let best = -1;
    let bestScore = Infinity;
    let bestSide = 0;
    let bestDist = Infinity;
    const l = this._seatLocal;
    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i)) continue;
      const c = Math.cos(this.yaw[i]);
      const sn = Math.sin(this.yaw[i]);
      for (let side = 0; side < 2; side++) {
        this.doorLocal(this.arch[i], side, l);
        const wx = this.x[i] + l.x * c + l.z * sn;
        const wz = this.z[i] - l.x * sn + l.z * c;
        const raw = Math.hypot(x - wx, z - wz);
        if (raw > maxDist) continue;
        const score = side === 0 ? raw - TRAFFIC.jackDriverBias : raw;
        if (score < bestScore) {
          bestScore = score;
          bestDist = raw;
          bestSide = side;
          best = i;
        }
      }
    }
    if (out) {
      out.side = bestSide;
      out.dist = best >= 0 ? bestDist : Infinity;
    }
    return best;
  }

  /**
   * Turns a live AI car into a PARKED slot at its current pose, keeping its
   * fleet handle, its archetype and its paint. THE CARJACK HANDOVER:
   * `systems/Driving.js#enterCar` takes a PARKED index and nothing else, so a
   * jacked car has to become one — the alternative is a second handover path
   * with its own collider and fleet bookkeeping, and two of those is how the
   * player's car ends up existing twice.
   *
   * The driving slot is then retired through {@link _deactivate}, which fires
   * {@link onDeactivate} exactly ONCE (a population director's cue to clear up
   * anyone still aboard) and gives back any junction claim. The hold and the
   * pin are cleared on the way out, so a carjack of a car that was already
   * stopped by somebody else — a driverless one, say — cannot leave a stopped
   * byte behind for the slot's next tenant.
   *
   * @param {number} i Driving slot.
   * @returns {number} Parked slot, or −1 when the guest reserve is exhausted
   *   AND nothing could be evicted (a headless pool of zero).
   */
  takeDriving(i) {
    if (!this._isDriving(i)) return -1;
    const p = this._allocParked();
    if (p < 0) return -1;
    const arch = this.arch[i];

    this.pState[p] = CAR_STATE.PARKED;
    this.pX[p] = this.x[i];
    this.pY[p] = this.y[i];
    this.pZ[p] = this.z[i];
    this.pYaw[p] = this.yaw[i];
    this.pArch[p] = arch;
    this.pPaint[p] = this.paint[i];
    this.pHandle[p] = this.handle[i];
    this.parked++;
    this.pDoor[p] = this._doorOf(p, arch);
    this._setParkedColliders(p, true);

    // Hand the fleet car over BEFORE the teardown, or `_deactivate` deletes it
    // and the player gets in a car that is not the one they were looking at.
    this.handle[i] = -1;
    this.hold[i] = 0;
    this.pinned[i] = 0;
    this._deactivate(i);

    this.fleet?.setSteer(this.pHandle[p], 0);
    // Every lamp, INCLUDING the sirens. A jacked cruiser is a car the player
    // owns, and a stolen police car that keeps flashing is a police car — the
    // tasteful default is that the blue lights are the response's, not yours.
    this.fleet?.setLamps(this.pHandle[p], { head: 0, brake: 0, sigL: 0, sigR: 0, siren: 0 });
    return p;
  }

  /**
   * A LIVE AI CAR IS DESTROYED: it stops driving and becomes a burnt-out parked
   * one, exactly where it died.
   *
   * ⚠ IT IS {@link takeDriving} PLUS ONE BYTE, and that is the whole design.
   * The transition a wreck needs — the fleet handle kept so the shell on screen
   * is the same car, the archetype and paint kept, the pose kept, the junction
   * claim given back, the hold and the pin cleared, and
   * {@link onDeactivate} fired EXACTLY ONCE so `systems/Population.js` gets its
   * occupants out — is character for character the transition a CARJACK needs,
   * and that path has been proved by `tests/carjack.test.mjs` since it shipped.
   * A second copy of it here would be a second handover path, and two of those
   * is how a car ends up existing twice (see {@link takeDriving}). So this is a
   * three-line wrapper on purpose; if it ever grows a fourth, the growth
   * belongs in `takeDriving`.
   *
   * @param {number} i Driving slot.
   * @returns {number} The parked slot the wreck now stands in, or −1 when the
   *   slot was not a live driving car or the guest reserve could not be freed.
   *   ⚠ A −1 leaves the car DRIVING and undamaged as far as this director is
   *   concerned — the caller has a live car it thinks is dead, which is the one
   *   failure worth checking for.
   */
  wreckDriving(i) {
    const p = this.takeDriving(i);
    if (p < 0) return -1;
    this.pWreck[p] = 1;
    return p;
  }

  /**
   * Hands a parked car to the player. Its colliders go away (the player is
   * inside it now) and the director stops touching its transform.
   * @param {number} i Parked slot.
   * @returns {{handle: number, arch: number, chassis: any, x: number, z: number,
   *   y: number, yaw: number, door: {x: number, z: number}}|null}
   */
  takeParked(i) {
    if (!(i >= 0 && i < this.parkedCap) || this.pState[i] !== CAR_STATE.PARKED) return null;
    this.pState[i] = CAR_STATE.PLAYER;
    this.parked--;
    this._setParkedColliders(i, false);
    return {
      handle: this.pHandle[i],
      arch: this.pArch[i],
      chassis: this.archChassis[this.pArch[i]],
      x: this.pX[i],
      y: this.pY[i],
      z: this.pZ[i],
      yaw: this.pYaw[i],
      door: this.pDoor[i],
    };
  }

  /**
   * Moves a car that is ALREADY parked, colliders and fleet transform included.
   *
   * It exists for one caller and one reason: {@link takeDriving} parks a car
   * exactly where it stopped, and the wanted-level response needs to slide the
   * cruiser its crew just got out of the last two metres to the kerb. Doing
   * that by re-registering colliders at the call site would put grid
   * bookkeeping in a gameplay system; doing it before `takeDriving` would move
   * a car that is still in the driving pool and still somebody's leader.
   *
   * @param {number} i Parked slot.
   * @param {number} x @param {number} y @param {number} z @param {number} yaw
   * @returns {boolean} False when the slot is not currently parked.
   */
  placeParked(i, x, y, z, yaw) {
    if (!(i >= 0 && i < this.parkedCap) || this.pState[i] !== CAR_STATE.PARKED) return false;
    this.pX[i] = x;
    this.pY[i] = y;
    this.pZ[i] = z;
    this.pYaw[i] = yaw;
    this._setParkedColliders(i, true);
    this.fleet?.setCarTransform(this.pHandle[i], x, y, z, yaw);
    return true;
  }

  /**
   * @param {number} i Parked slot.
   * @returns {boolean} Still standing there — false once the player has taken
   *   it ({@link takeParked}) or the ring has collected it.
   */
  isParked(i) {
    return i >= 0 && i < this.parkedCap && this.pState[i] === CAR_STATE.PARKED;
  }

  /**
   * Marks (or unmarks) a parked slot as a WRECK. See {@link pWreck}.
   *
   * It takes any live parked slot, INCLUDING one in {@link CAR_STATE}`.PLAYER`:
   * the car the player is sitting in when it blows up is still theirs for the
   * step it takes them to get out of it, and the byte has to be set on the slot
   * that will hold the burnt-out shell — `returnParked` never touches it, so
   * marking it early and letting the exit run is the ordering that works from
   * either direction (see `systems/Gameplay.js`'s `'car:wrecked'` handler).
   *
   * This director has no opinion about what a wreck LOOKS like — that is the
   * damage system's and the fleet's — and none about how one comes to be. All
   * it owns is the consequence: a burnt-out car is furniture.
   *
   * @param {number} i Parked slot.
   * @param {boolean} on
   * @returns {boolean} False when the slot index is out of range.
   */
  setWrecked(i, on) {
    if (!(i >= 0 && i < this.parkedCap)) return false;
    this.pWreck[i] = on ? 1 : 0;
    return true;
  }

  /** @param {number} i Parked slot. @returns {boolean} */
  isWrecked(i) {
    return i >= 0 && i < this.parkedCap && this.pWreck[i] === 1;
  }

  /**
   * COLLECTS A PARKED CAR ON DEMAND: colliders off, fleet car released, slot
   * FREE, bay binding dropped — everything the despawn ring does, at a moment
   * of somebody else's choosing.
   *
   * ⚠ IT EXISTS BECAUSE A TIMED REMOVAL HAS NO RING TO RIDE. Every other way a
   * parked car leaves the world is a DISTANCE question this director asks
   * itself ({@link _repark} at `TRAFFIC.parkDespawn`, {@link _recycle} at
   * `TRAFFIC.despawn`), so the whole collection path was private and nothing
   * outside could reach it. A burnt-out shell that has to disappear sixty
   * seconds after it burned is the first caller with a clock instead of a
   * radius — `systems/VehicleDamage.js` starts the timer, announces
   * `'car:despawn'`, and `systems/Gameplay.js` calls this. The alternative was
   * a gameplay system doing grid, fleet and bay bookkeeping at the call site,
   * which is exactly what {@link _freeParked}'s "one place the binding is
   * dropped" note exists to prevent.
   *
   * It DELEGATES rather than duplicates, and that is not tidiness: `_freeParked`
   * clears {@link pWreck}, releases the shape ids, drops the bay binding and
   * calls `fleet.removeCar` in one place, and a second teardown path is how a
   * fleet car outlives the slot that owned it.
   *
   * TWO REFUSALS, both answering false rather than throwing:
   *  • {@link CAR_STATE}`.PLAYER` — the player is sitting in it (or coasting out
   *    of it after a bail). Deleting the car somebody is inside is the one
   *    unrecoverable state this whole feature can produce, so the refusal lives
   *    HERE as well as at the call site: a gate only at the call site is a gate
   *    the next caller forgets.
   *  • `FREE` — nothing to collect. That is the NORMAL answer for a timer that
   *    fires on a car the ring already took, not an error.
   *
   * Idempotent by construction: the first call leaves the slot FREE, so the
   * second one takes the second refusal.
   *
   * @param {number} i Parked slot.
   * @returns {boolean} True when this call is what removed the car.
   */
  despawnParked(i) {
    if (!(i >= 0 && i < this.parkedCap)) return false;
    const st = this.pState[i];
    if (st === CAR_STATE.FREE || st === CAR_STATE.PLAYER) return false;
    this._freeParked(i);
    return true;
  }

  /**
   * Parks the player's car wherever they left it. The colliders come back, so
   * an abandoned car is world geometry again.
   * @param {number} i Parked slot from {@link takeParked}.
   * @param {number} x @param {number} y @param {number} z @param {number} yaw
   * @returns {boolean}
   */
  returnParked(i, x, y, z, yaw) {
    if (!(i >= 0 && i < this.parkedCap) || this.pState[i] !== CAR_STATE.PLAYER) return false;
    this.pX[i] = x;
    this.pY[i] = y;
    this.pZ[i] = z;
    this.pYaw[i] = yaw;
    this.pState[i] = CAR_STATE.PARKED;
    this.parked++;
    this._setParkedColliders(i, true);
    this.fleet?.setCarTransform(this.pHandle[i], x, y, z, yaw);
    this.fleet?.setSteer(this.pHandle[i], 0);
    this.fleet?.setLamps(this.pHandle[i], { head: 0, brake: 0, sigL: 0, sigR: 0, siren: 0 });
    return true;
  }

  // ── population ────────────────────────────────────────────────────────────

  /**
   * Builds the bay layout and fills the ones around the current focus. Call
   * ONCE, at level start, AFTER {@link setFocus} and before the first `update`
   * (that is the order `systems/Gameplay.js` uses). Deterministic: same seed,
   * same cars in the same bays, whatever route the player then takes.
   *
   * ⚠ The initial fill reaches out to {@link TRAFFIC.parkDespawn}, not to
   * `parkRadius`. A level has to START with its whole budget on the ground —
   * anything placed inside the OUTER radius is a car the ring will keep rather
   * than immediately collect, so there is no build-then-delete churn on the
   * first step. Every later pass fills within the inner radius, which is the
   * hysteresis (see {@link _repark}).
   *
   * @returns {number} Cars parked.
   */
  placeParkedCars() {
    if (!this._bayN) this._buildBays();
    this._repark(TRAFFIC.parkDespawn);
    return this.parked;
  }

  /**
   * THE LAYOUT, ONCE. Every legal bay in the level, each carrying the car it
   * will hold whenever it is in range.
   *
   * ⚠ ARCHETYPE, PAINT AND OCCUPANCY ARE HASHED FROM THE BAY, NOT DRAWN. The
   * old code walked the bay list pulling numbers off `this._rng`, so a bay's
   * car depended on the ORDER bays were filled — fine when that happened once
   * at level start, fatal the moment bays fill and empty as the player wanders,
   * because a bay would come back a different car every time and the city would
   * reshuffle behind them. Each bay instead seeds its own `mulberry32` from
   * (level seed, bay identity), and its identity is its index in this list
   * PLUS its quantised position: a bay is the same car only if it is the same
   * bay in the same place. Nothing here touches a shared sequential stream, so
   * the traffic stream's draw order — which `_trySpawn` and `_activate` pin —
   * is exactly what it was.
   *
   * The OCCUPANCY gate is stratified rather than a coin flip per bay; see
   * {@link TRAFFIC.parkStride} for why the exactness matters.
   */
  _buildBays() {
    const bays = this._parkingSlots();
    const n = bays.city.length + bays.country.length;
    this._bayX = new Float32Array(n);
    this._bayZ = new Float32Array(n);
    this._bayYaw = new Float32Array(n);
    this._bayArch = new Uint8Array(n);
    this._bayPaint = new Uint8Array(n);
    this._bayGate = new Uint8Array(n);
    this._bayCountry = new Uint8Array(n);
    this._baySlot = new Int32Array(n).fill(-1);
    this._bayCand = new Int32Array(n);
    this._bayCandD = new Float64Array(n);

    // A DEDICATED root (rule 4). Adding a stage upstream may not re-colour one
    // parked car, and the parking rows may not perturb one driving one.
    const root = (hashSeed(this.seed) + hashSeed('traffic.park')) >>> 0;
    /** @type {Array<[Array<{x: number, z: number, yaw: number}>, number, string]>} */
    const lists = [
      [bays.city, TRAFFIC.parkStride, 'city'],
      [bays.country, TRAFFIC.parkCountryStride, 'verge'],
    ];
    let at = 0;
    let cars = 0;
    for (const [list, stride, tag] of lists) {
      for (let k = 0; k < list.length; k++) {
        const c = list[k];
        // Quantised to 1 cm. The identity has to survive a Float32 round trip
        // and a recompile of the same map, and nothing finer than a centimetre
        // is a different bay.
        const key = `${tag}:${k}@${Math.round(c.x * 100)},${Math.round(c.z * 100)}`;
        const r = mulberry32((root + hashSeed(key)) >>> 0);
        this._bayX[at] = c.x;
        this._bayZ[at] = c.z;
        this._bayYaw[at] = c.yaw;
        const isDirt = c.cls === 'dirt';
        // `civArch`, never `archKeys.length`: the kerbs are civilian rows, and
        // a police cruiser parked in one would be a wanted level nobody earned.
        // A DIRT bay draws from the truck pool only — same rule the driving
        // ring's `_activate` applies — and still spends exactly one number
        // from `r` either way, so the paint draw right after it is unmoved.
        this._bayArch[at] = isDirt && this._truckArch.length
          ? this._truckArch[(r() * this._truckArch.length) | 0]
          : (r() * this.civArch) | 0;
        this._bayPaint[at] = (r() * PAINTS.length) | 0;
        this._bayCountry[at] = tag === 'city' ? 0 : 1;
        // ...and the stratum draw is keyed on the STRATUM, so which bay of a
        // block is occupied cannot depend on any bay's own numbers.
        const g = (k / stride) | 0;
        const pick = (mulberry32((root + hashSeed(`${tag}#${g}`)) >>> 0)() * stride) | 0;
        let on = k % stride === pick ? 1 : 0;
        // DIRT VERGES ARE QUIET (TRAFFIC.parkDirtKeep). A SECOND, independent
        // thinning on top of the stratum gate above, so a dirt bay this passed
        // is still mostly turned back off — a dusty verge holds a rare parked
        // truck, not a parking lane. Every non-dirt bay never reaches this
        // branch, so its occupancy is exactly what the stratum gate alone has
        // always produced.
        if (on && isDirt) {
          const thin = mulberry32((root + hashSeed(`${tag}:dirtThin:${k}`)) >>> 0)();
          if (thin >= TRAFFIC.parkDirtKeep) on = 0;
        }
        this._bayGate[at] = on;
        cars += on;
        at++;
      }
    }
    this._bayN = n;
    this._bayCars = cars;
  }

  /**
   * ONE RING PASS: collect every budgeted car that has fallen out of
   * {@link TRAFFIC.parkDespawn}, then fill the nearest empty bays inside
   * `placeRadius` until the `maxParked` budget is spent.
   *
   * Three slots are furniture to nobody and are never touched:
   *  • a slot in {@link CAR_STATE}`.PLAYER` — the player is driving it (or
   *    coasting out of it after a bail). Collecting that is deleting the car
   *    somebody is sitting in;
   *  • a GUEST slot (`i >= maxParked`) — those are the player's abandoned cars
   *    and they ride their own ring in {@link _recycle}, at `TRAFFIC.despawn`,
   *    exactly as they did before this existed;
   *  • the DRIVING pool, pins included. A pinned car is mid-carjack and belongs
   *    to `systems/Carjack.js`; nothing in here can see it.
   *
   * ⚠ THE DUPLICATE CASE. Drive a bay's car away and abandon it three districts
   * over and its slot is still BOUND to its bay — the cull tests the CAR's
   * position, not the bay's, so a car parked next to the player survives
   * wherever it came from, and the bay it came out of stays empty for as long
   * as that car exists. Walk far enough away and the same rule that collects
   * any distant car collects it, the binding is released, and the bay is free
   * to hold its own car again — the same one it always holds, because that is a
   * property of the bay. Without the binding the bay would refill the moment
   * the player drove off and they would own a car that was also still parked
   * where they found it.
   *
   * NOBODY IS EVER LEFT IN ONE: a parked car has no occupants to strand.
   * `Population` seats people through {@link onActivate}/{@link getSeatWorld},
   * and both are DRIVING-slot only (`getSeatWorld` returns null for anything
   * else) — a carjack takes its victim out through `Carjack`'s own seams before
   * the car ever becomes a parked slot.
   *
   * @param {number} placeRadius Metres. Bays further than this are left empty.
   */
  _repark(placeRadius) {
    const n = this._bayN;
    if (!n) return;
    const f = this._focus;
    this._parkX = f.x;
    this._parkZ = f.z;

    // ── 1. collect ────────────────────────────────────────────────────────────
    const cull2 = TRAFFIC.parkDespawn * TRAFFIC.parkDespawn;
    for (let b = 0; b < n; b++) {
      const slot = this._baySlot[b];
      if (slot < 0) continue;
      const st = this.pState[slot];
      if (st === CAR_STATE.PLAYER) continue;
      // FREE means something else already took it apart (teardown, a guest
      // eviction); `_freeParked` is idempotent and drops the binding.
      if (st === CAR_STATE.FREE) {
        this._freeParked(slot);
        continue;
      }
      const dx = this.pX[slot] - f.x;
      const dz = this.pZ[slot] - f.z;
      if (dx * dx + dz * dz <= cull2) continue;
      this._freeParked(slot);
      this.parkCulled++;
    }

    // ── 2. fill, NEAREST FIRST ────────────────────────────────────────────────
    // Nearest first is what keeps the budget's frontier at the OUTER edge of the
    // ring: when `maxParked` runs out it runs out on the bays furthest from the
    // camera, so the one bay that pops is always the one nobody can see.
    let live = 0;
    for (let i = 0; i < this.maxParked; i++) if (this.pState[i] !== CAR_STATE.FREE) live++;
    if (live >= this.maxParked) return;

    const r2 = placeRadius * placeRadius;
    let m = 0;
    for (let b = 0; b < n; b++) {
      if (!this._bayGate[b] || this._baySlot[b] >= 0) continue;
      const dx = this._bayX[b] - f.x;
      const dz = this._bayZ[b] - f.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > r2) continue;
      this._bayCand[m] = b;
      this._bayCandD[m] = d2;
      m++;
    }
    while (live < this.maxParked && m > 0) {
      let best = 0;
      for (let k = 1; k < m; k++) if (this._bayCandD[k] < this._bayCandD[best]) best = k;
      const b = this._bayCand[best];
      m--;
      this._bayCand[best] = this._bayCand[m];
      this._bayCandD[best] = this._bayCandD[m];
      const slot = this._allocBay();
      if (slot < 0) break;
      this._initParked(slot, this._bayX[b], this._bayZ[b], this._bayYaw[b], this._bayArch[b], this._bayPaint[b]);
      this._baySlot[b] = slot;
      this._slotBay[slot] = b;
      this.parkPlaced++;
      live++;
    }
  }

  /**
   * A free BUDGETED slot. The mirror of {@link _allocParked}, which hands out
   * guest slots and only guest slots — the two pools never trade, which is what
   * keeps "abandoning cars can never grow the fleet" true from both ends.
   * @returns {number} Parked slot, or −1.
   */
  _allocBay() {
    for (let i = 0; i < this.maxParked; i++) if (this.pState[i] === CAR_STATE.FREE) return i;
    return -1;
  }

  /**
   * @param {number} b Bay index.
   * @returns {{x: number, z: number, yaw: number, arch: number, paint: number,
   *   country: boolean, occupied: boolean, slot: number}|null} What the layout
   *   says about one bay. Published for the tests and for a future debug
   *   overlay; the director itself reads the arrays.
   */
  bayInfo(b) {
    if (!this._bayN || !(b >= 0 && b < this._bayN)) return null;
    return {
      x: this._bayX[b],
      z: this._bayZ[b],
      yaw: this._bayYaw[b],
      arch: this._bayArch[b],
      paint: this._bayPaint[b],
      country: this._bayCountry[b] === 1,
      occupied: this._bayGate[b] === 1,
      slot: this._baySlot[b],
    };
  }

  /**
   * Puts a car on a named lane at a named arc length, bypassing the spawn ring.
   * The director never calls this itself — it exists for SCRIPTED content (a
   * mission convoy, a police unit joining traffic) and for the tests, which
   * need a repeatable four-way cross rather than whatever the ring produced.
   *
   * @param {number} edgeId @param {number} s Metres along the edge.
   * @param {{arch?: number, reserved?: boolean}} [o] `arch` names an ARCHETYPE
   *   instead of rolling one ({@link archIndexOf}); `reserved` tells a
   *   population director that this car's seats belong to the caller. Both are
   *   for authored content — the ring never passes either.
   * @returns {number} Driving slot, or −1 when the pool is full.
   */
  spawnAt(edgeId, s, o = null) {
    const e = this.graph.edges[edgeId];
    if (!e || e.kind !== 'lane') return -1;
    const slot = this._allocDriving();
    if (slot < 0) return -1;
    this._activate(slot, edgeId, Math.max(0, Math.min(e.length, s)), o);
    return slot;
  }

  /**
   * One fixed step. Allocation-free.
   * @param {number} dt Seconds.
   */
  update(dt) {
    if (this._disposed || !(dt > 0)) return;
    this.clock += dt;

    if (this.autoPopulate) {
      this._refreshCandidates();
      this._recycle();
      // The parked rows ride their own, wider ring. Throttled on focus travel
      // like the candidate cache above — a pass is ~620 distance tests and
      // there is nothing to see in doing that per frame.
      if (this._bayN && !(Math.hypot(this._focus.x - this._parkX, this._focus.z - this._parkZ) < TRAFFIC.parkRefresh)) {
        this._repark(TRAFFIC.parkRadius);
      }
    }
    this._expireClaims(dt);
    this._grantClaims();

    const g = this.graph;
    const blink = ((this.clock * TRAFFIC.blinkHz) % 1) < TRAFFIC.blinkDuty;
    const night = this._getNight ? this._getNight() : 0;
    const head = night > TRAFFIC.nightOn ? 1 : 0;

    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i)) continue;
      const e = g.edges[this.edge[i]];
      if (!e) {
        this._deactivate(i);
        continue;
      }

      // ── 1. desired speed = the lowest constraint ──────────────────────────
      // A pursuit car drives the SAME lane harder: every limit below is scaled,
      // including the slow-into-the-next-edge curve, so blue lights never turn
      // a right-angle fillet into a slide.
      const scale = this._speedScale(i);
      let want = this.hold[i] ? 0 : e.speedLimit * scale;
      if (!this.hold[i]) {
        const lead = this._leaderGap(i);
        if (lead < Infinity) want = Math.min(want, brakeCurve(lead - TRAFFIC.headway));
        const actor = this._actorAhead(i);
        if (actor < Infinity) want = Math.min(want, brakeCurve(actor - TRAFFIC.actorStop));
        if (this.state[i] === CAR_STATE.APPROACH && this.claim[i] < 0 && this.want[i] >= 0) {
          want = Math.min(want, brakeCurve(e.length - TRAFFIC.stopGap - this.s[i]));
        }
        // Slow INTO the next edge, not on it: a turn link is capped at
        // TURN_SPEED and a car that only discovers that on entry spends the
        // first third of the corner braking sideways.
        const nxt = this.nextEdge[i] >= 0 ? g.edges[this.nextEdge[i]] : null;
        if (nxt && nxt.speedLimit * scale < want) {
          want = Math.min(want, brakeCurve(e.length - this.s[i], nxt.speedLimit * scale));
        }
      }

      // ── 2. longitudinal ───────────────────────────────────────────────────
      const before = this.v[i];
      if (want > this.v[i]) this.v[i] = Math.min(want, this.v[i] + TRAFFIC.accel * dt);
      else this.v[i] = Math.max(want, this.v[i] - TRAFFIC.brake * dt);
      if (this.v[i] < 0) this.v[i] = 0;
      this.decel[i] = this.v[i] < before - 0.02 ? 1 : 0;

      // ── 3. advance along the lane ─────────────────────────────────────────
      this.s[i] += this.v[i] * dt;
      let guard = 4;
      while (this.s[i] >= g.edges[this.edge[i]].length && guard-- > 0) {
        if (!this._advanceEdge(i)) break;
      }
      if (!this._isDriving(i)) continue;

      this._planAhead(i);

      // ── 4. pose ───────────────────────────────────────────────────────────
      const cur = g.edges[this.edge[i]];
      g.sampleAt(cur.id, this.s[i], this._sample);
      this.x[i] = this._sample.x;
      this.z[i] = this._sample.z;
      this.y[i] = this.heightAt(this._sample.x, this._sample.z);

      // ⚠ Yaw comes from a chord CENTRED on the car (owner playtest round 1),
      // not from pure pursuit. Aiming `4 + v/2` m ahead made the yaw lead the
      // tangent at the centre by half the lookahead, so a cornering car read
      // as pivoting about its NOSE — tail sweeping wide, nose glued to the
      // lane. The chord from `s − yawChord` to `s + yawChord` has the car in
      // its middle, so the body rotates about its own centre; the behind
      // sample clamps at the edge start, which biases the first metre of a
      // link by centimetres and nothing more.
      this._lookAhead(i, TRAFFIC.yawChord, this._sample2);
      g.sampleAt(cur.id, this.s[i] - TRAFFIC.yawChord, this._sample3);
      const tx = this._sample2.x - this._sample3.x;
      const tz = this._sample2.z - this._sample3.z;
      const wantYaw = Math.abs(tx) + Math.abs(tz) > 1e-4
        ? yawFor(tx, tz)
        : yawFor(this._sample.dx, this._sample.dz);
      const delta = wrapPi(wantYaw - this.yaw[i]);
      const maxTurn = TRAFFIC.yawRate * dt;
      const applied = delta > maxTurn ? maxTurn : delta < -maxTurn ? -maxTurn : delta;
      this.yaw[i] += applied;
      // Front wheels from the yaw rate, inverted bicycle model — a visibly
      // straight-ahead wheel on a car rounding a corner is the tell that the
      // renderer is not being driven by the sim.
      const ch = this.archChassis[this.arch[i]];
      const rate = dt > 0 ? applied / dt : 0;
      const steer = clamp((rate * ch.wheelbase) / Math.max(1.5, this.v[i]) / STEER_MAX, -1, 1);
      this.steer[i] = steer;

      // ── 5. signals + lamps ────────────────────────────────────────────────
      this._updateSignal(i);
      const L = this._lampState;
      L.head = head;
      L.brake = this.decel[i] || (this.v[i] < 0.2 && want < 0.2) ? 1 : head ? 0.5 : 0;
      L.sigL = this.sig[i] < 0 && blink ? 1 : 0;
      L.sigR = this.sig[i] > 0 && blink ? 1 : 0;

      const h = this.handle[i];
      if (h >= 0 && this.fleet) {
        this.fleet.setCarTransform(h, this.x[i], this.y[i], this.z[i], this.yaw[i]);
        this.fleet.setSteer(h, steer);
        this.fleet.advance(h, this.v[i] * dt);
        this.fleet.setLamps(h, L);
      }
    }

    if (this.autoPopulate) this._populate();
    this.fleet?.update();
  }

  /**
   * ⚠ `archetypes` is the CIVILIAN pool ({@link civArch}) — the number the
   * spawn ring rolls against and the one `tests/traffic.test.mjs` pins. The
   * authored law bodies are counted separately, because "how many kinds of car
   * does this city's traffic have" and "does it own a cruiser" are different
   * questions.
   *
   * ⚠ AND THE PARKED ROWS ARE A BUDGET NOW, so they are reported like one (the
   * house rule `Traffic.stats().draws` and `RigEnemyRenderer#partsPerType`
   * exist for). `bays` is the whole layout, `bayCars` how many of them the
   * layout ever fills, `baysLive` how many are standing in the world THIS
   * instant — that last one against `maxParked` is the number that says
   * whether the ring is doing its job, and `placed`/`culled` are its churn.
   *
   * ⚠ `wrecks` counts the whole pool, guest slots included, because that is
   * where they mostly are: a car destroyed while DRIVING lands in the guest
   * reserve through {@link wreckDriving}, and one destroyed while parked stays
   * in its bay. A number that only ever grows is the tell that the despawn ring
   * has stopped collecting them.
   *
   * @returns {{driving: number, parked: number, archetypes: number,
   *   lawArchetypes: number, pursuing: number, draws: number, claims: number,
   *   cars: number, bays: number, bayCars: number, baysLive: number,
   *   bayBudget: number, placed: number, culled: number, wrecks: number}}
   */
  stats() {
    let claims = 0;
    for (let i = 0; i < this.claimOwner.length; i++) if (this.claimOwner[i] >= 0) claims++;
    let pursuing = 0;
    for (let i = 0; i < this.top; i++) if (this.isPursuing(i)) pursuing++;
    let baysLive = 0;
    for (let i = 0; i < this.maxParked; i++) if (this.pState[i] !== CAR_STATE.FREE) baysLive++;
    let wrecks = 0;
    for (let i = 0; i < this.parkedCap; i++) if (this.pState[i] !== CAR_STATE.FREE && this.pWreck[i]) wrecks++;
    const fs = this.fleet?.stats?.() ?? null;
    return {
      driving: this.driving,
      parked: this.parked,
      archetypes: this.civArch,
      lawArchetypes: this.archKeys.length - this.civArch,
      pursuing,
      draws: fs?.drawCalls ?? 0,
      cars: fs?.cars ?? this.driving + this.parked,
      claims,
      bays: this._bayN,
      bayCars: this._bayCars,
      baysLive,
      bayBudget: this.maxParked,
      placed: this.parkPlaced,
      culled: this.parkCulled,
      wrecks,
    };
  }

  /** Idempotent. Releases every fleet handle and every collider it registered. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    // Drop the population seam FIRST: teardown frees every slot by hand (it
    // does not go through `_deactivate`), and a director that is already gone
    // must not be called back into.
    this.onActivate = null;
    this.onDeactivate = null;
    for (let i = 0; i < this.top; i++) {
      if (this.handle[i] >= 0) this.fleet?.removeCar(this.handle[i]);
      this.handle[i] = -1;
      this.state[i] = CAR_STATE.FREE;
    }
    for (let i = 0; i < this.parkedCap; i++) {
      if (this.pState[i] === CAR_STATE.FREE) continue;
      this._setParkedColliders(i, false);
      if (this.pHandle[i] >= 0) this.fleet?.removeCar(this.pHandle[i]);
      this.pHandle[i] = -1;
      this.pState[i] = CAR_STATE.FREE;
      // Teardown frees every slot BY HAND rather than through `_freeParked`
      // (see the note at the top of this method), so the byte has to be cleared
      // here too or a director reused across a level rebuild would inherit a
      // pool of wrecks.
      this.pWreck[i] = 0;
    }
    this.driving = 0;
    this.parked = 0;
    if (this._ownsFleet) this.fleet?.dispose();
    this.fleet = null;
  }

  // ── internals: population ─────────────────────────────────────────────────

  /**
   * @param {number} i Driving slot.
   * @returns {boolean} True while the slot holds a live AI car. Public because
   *   `systems/Driving.js` walks the pool to shunt cars, and the carjack task
   *   will walk it to pick a target.
   */
  isDriving(i) {
    const st = this.state[i];
    return st === CAR_STATE.CRUISE || st === CAR_STATE.APPROACH || st === CAR_STATE.CROSS;
  }

  /** @param {number} i @returns {boolean} */
  _isDriving(i) {
    return this.isDriving(i);
  }

  /** Retires anything that has wandered out of the despawn ring. */
  _recycle() {
    const f = this._focus;
    const r2 = TRAFFIC.despawn * TRAFFIC.despawn;
    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i)) continue;
      const dx = this.x[i] - f.x;
      const dz = this.z[i] - f.z;
      // A car mid-junction owns the box; retiring it there would strand the
      // claim for a frame and let two cars into the same crossing. A PINNED
      // car is mid-carjack and belongs to the sequence until it says so.
      if (dx * dx + dz * dz > r2 && this.claim[i] < 0 && !this.pinned[i]) this._deactivate(i);
    }
    // GUEST parked slots — cars the player took off the road and abandoned —
    // ride the same ring as the driving pool, at `TRAFFIC.despawn`. A jacked car
    // left three districts away is litter, and without this the reserve drains
    // one slot per carjack and the fleet grows forever.
    //
    // ⚠ The BUDGETED bays are not in this loop, and it is not because they never
    // retire (they do now — see `_repark`). They ride a WIDER ring on their own
    // cadence, because furniture the player can walk back to has to outlast
    // traffic that is allowed to have driven off.
    for (let i = this.maxParked; i < this.parkedCap; i++) {
      if (this.pState[i] !== CAR_STATE.PARKED) continue;
      const dx = this.pX[i] - f.x;
      const dz = this.pZ[i] - f.z;
      if (dx * dx + dz * dz > r2) this._freeParked(i);
    }
  }

  /** Tops the driving population up to the ring's target. */
  _populate() {
    const target = this._target();
    let tries = TRAFFIC.tries;
    while (this.driving < target && tries-- > 0) this._trySpawn();
  }

  /** @returns {number} How many cars this stretch of city should hold. */
  _target() {
    const t = this._supply / TRAFFIC.supplyFull;
    const k = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.round(TRAFFIC.activeMin + (TRAFFIC.activeMax - TRAFFIC.activeMin) * k);
  }

  /**
   * Lane edges whose midpoint sits in the spawn annulus, cached until the focus
   * has moved far enough to matter. Also counts the wider supply, which is what
   * decides whether this is a downtown grid or one country road.
   *
   * ⚠ THE WEIGHTING LIVES HERE, NOT IN `_trySpawn`. `_candCum` is the running
   * sum of {@link TRAFFIC.classWeight} over the candidate list, so `pick` (a
   * plain `[0,1)` draw `_trySpawn` already made) can be mapped onto a SKEWED
   * edge by {@link _pickCandidate} without spending a single extra number on
   * it — the rejection a low weight amounts to happens by never landing in
   * that edge's slice of the line, before any dice are spent asking "is this
   * one kept". A class missing from the table (every city class, `country`)
   * gets weight 1 by default, and `_candCum[i] = i + 1` for an all-1 list is
   * exactly the old unweighted index — see the negative control in
   * `tests/traffic.test.mjs`.
   */
  _refreshCandidates() {
    const f = this._focus;
    if (Math.hypot(f.x - this._candX, f.z - this._candZ) < TRAFFIC.candidateRefresh) return;
    this._candX = f.x;
    this._candZ = f.z;
    const lanes = this.graph.laneEdges;
    const edges = this.graph.edges;
    const weights = TRAFFIC.classWeight;
    const lo = (TRAFFIC.spawnMin - 20) ** 2;
    const hi = (TRAFFIC.spawnMax + 20) ** 2;
    const sup = TRAFFIC.despawn * TRAFFIC.despawn;
    let n = 0;
    let supply = 0;
    let wsum = 0;
    for (let i = 0; i < lanes.length; i++) {
      const dx = this._midX[i] - f.x;
      const dz = this._midZ[i] - f.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < sup) supply++;
      if (d2 >= lo && d2 <= hi) {
        const laneId = lanes[i];
        const w = weights[edges[laneId].cls] ?? 1;
        this._candidates[n] = laneId;
        wsum += w;
        this._candCum[n] = wsum;
        n++;
      }
    }
    this._candN = n;
    this._candWSum = wsum;
    this._supply = supply;
  }

  /**
   * Weighted pick over `_candidates[0.._candN)` from a single `[0,1)` draw,
   * using the cumulative weight `_refreshCandidates` built. Binary search over
   * a monotonic prefix sum — the standard "point in a weighted line" trick.
   *
   * ⚠ WITH EVERY CANDIDATE AT THE DEFAULT WEIGHT (1), THIS IS `(pick * candN)
   * | 0`, IDENTICALLY. `_candCum[k] = k + 1` then, so the smallest `k` with
   * `_candCum[k] > pick * candN` is `Math.floor(pick * candN)` for every
   * `pick` in `[0,1)` — the exact index the old unweighted line used. That
   * identity is WHY the weighting could be added without touching
   * `_trySpawn`'s draw count: a class left at the default is bit-for-bit what
   * it was.
   * @param {number} pick In `[0, 1)`.
   * @returns {number} A lane edge id.
   */
  _pickCandidate(pick) {
    const target = pick * this._candWSum;
    let lo = 0;
    let hi = this._candN - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this._candCum[mid] > target) hi = mid;
      else lo = mid + 1;
    }
    return this._candidates[lo];
  }

  /**
   * One spawn attempt. ALWAYS draws the same two numbers, whatever the outcome,
   * so a rejected candidate cannot reshuffle the stream (rule 4's corollary).
   * @returns {boolean}
   */
  _trySpawn() {
    const rng = this._rng;
    const pick = rng();
    const along = rng();
    if (this._candN === 0 || !(this._candWSum > 0)) return false;
    const eid = this._pickCandidate(pick);
    const e = this.graph.edges[eid];
    if (!e || e.length < 6) return false;
    const s = 2 + along * (e.length - 4);

    this.graph.sampleAt(eid, s, this._sample);
    const f = this._focus;
    const dx = this._sample.x - f.x;
    const dz = this._sample.z - f.z;
    const d = Math.hypot(dx, dz);
    if (d < TRAFFIC.spawnMin || d > TRAFFIC.spawnMax) return false;

    // Never inside the camera's forward cone: a car that materialises on screen
    // is the one traffic bug a player always notices.
    const fx = -Math.sin(f.yaw);
    const fz = -Math.cos(f.yaw);
    if ((dx / d) * fx + (dz / d) * fz > Math.cos(TRAFFIC.coneDeg * DEG)) return false;

    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i) || this.edge[i] !== eid) continue;
      if (Math.abs(this.s[i] - s) < TRAFFIC.spawnClear) return false;
    }

    const slot = this._allocDriving();
    if (slot < 0) return false;
    this._activate(slot, eid, s);
    return true;
  }

  /** @returns {number} */
  _allocDriving() {
    for (let i = 0; i < this.maxDriving; i++) {
      if (this.state[i] === CAR_STATE.FREE) {
        if (i >= this.top) this.top = i + 1;
        return i;
      }
    }
    return -1;
  }

  /**
   * A free GUEST parked slot ({@link TRAFFIC.guestParked}). Never a budgeted
   * bay: those hold the level's own parked rows and handing one out would
   * delete a car the player can see. When the whole reserve is taken the
   * FURTHEST guest car is evicted — one abandoned across the island is the one
   * nobody will miss, and a carjack must never simply fail.
   * @returns {number} Parked slot, or −1.
   */
  _allocParked() {
    for (let i = this.maxParked; i < this.parkedCap; i++) {
      if (this.pState[i] === CAR_STATE.FREE) return i;
    }
    let worst = -1;
    let worstD = -1;
    for (let i = this.maxParked; i < this.parkedCap; i++) {
      if (this.pState[i] !== CAR_STATE.PARKED) continue;
      const dx = this.pX[i] - this._focus.x;
      const dz = this.pZ[i] - this._focus.z;
      const d = dx * dx + dz * dz;
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst < 0) return -1;
    this._freeParked(worst);
    return worst;
  }

  /**
   * Releases a parked slot: colliders off, fleet car gone, slot FREE. The
   * shape ids in `pCol0`/`pCol1` are KEPT — `_setParkedColliders` re-points
   * them with `setCircle` when the slot is reused, so recycling a guest bay
   * costs no grid allocation.
   *
   * Safe on a BUDGETED slot as well as a guest one, and it is the only place
   * the bay binding is dropped — which is what makes "this bay's car exists
   * somewhere" a question with one answer wherever the release came from
   * (the ring, a teardown, an eviction).
   * @param {number} i
   */
  _freeParked(i) {
    const b = this._slotBay[i];
    if (b >= 0) {
      this._slotBay[i] = -1;
      if (this._baySlot) this._baySlot[b] = -1;
    }
    // Before the FREE early-out, not after: an already-free slot that somehow
    // still carries the byte has to be able to shed it, and a clear is cheaper
    // than reasoning about whether that can happen.
    this.pWreck[i] = 0;
    if (this.pState[i] === CAR_STATE.FREE) return;
    this._setParkedColliders(i, false);
    if (this.pHandle[i] >= 0) {
      this.fleet?.removeCar(this.pHandle[i]);
      this.pHandle[i] = -1;
    }
    if (this.pState[i] === CAR_STATE.PARKED) this.parked--;
    this.pState[i] = CAR_STATE.FREE;
  }

  /**
   * @param {number} i @param {number} eid @param {number} s
   * @param {{arch?: number, reserved?: boolean}|null} [o] See {@link spawnAt}.
   */
  _activate(i, eid, s, o = null) {
    const rng = this._rng;
    // ⚠ ALL THREE draws happen unconditionally, whatever the caller asked for.
    // An archetype override that skipped its draw would reshuffle every car the
    // ring spawns after it — the same house rule as `_trySpawn`'s, and the
    // reason the law's cars cost the traffic stream nothing.
    const rolled = (rng() * this.civArch) | 0;
    const paintIdx = (rng() * PAINTS.length) | 0;
    const paint = PAINTS[paintIdx];
    const turnSeed = (this._turnRoot() * 0xffffffff) >>> 0;
    let arch;
    if (o && o.arch >= 0) {
      arch = Math.min(this.archKeys.length - 1, o.arch | 0);
    } else if (this._truckArch.length && this.graph.edges[eid].cls === 'dirt') {
      // DIRT LANES ARE TRUCK COUNTRY (TRAFFIC.classWeight). No dice of its
      // own are spent: `rolled` already happened above and its RANGE is
      // simply repurposed as an index into the truck pool, so a dirt spawn
      // costs the traffic stream nothing extra and every OTHER class's rng
      // draw is exactly what it always was.
      arch = this._truckArch[rolled % this._truckArch.length];
    } else {
      arch = rolled;
    }

    this.state[i] = CAR_STATE.CRUISE;
    this.edge[i] = eid;
    this.nextEdge[i] = -1;
    this.s[i] = s;
    this.v[i] = this.graph.edges[eid].speedLimit * 0.75;
    this.claim[i] = -1;
    this.claimT[i] = 0;
    this.want[i] = -1;
    this.ticket[i] = 0;
    this.hold[i] = 0;
    this.pinned[i] = 0;
    this.sig[i] = 0;
    this.arch[i] = arch;
    this.paint[i] = paintIdx;
    this.mode[i] = DRIVE_MODE.AMBIENT;
    this.priority[i] = 0;
    this.goalX[i] = 0;
    this.goalZ[i] = 0;
    // Set BEFORE `onActivate` fires below, because that callback is exactly
    // where a population director decides whether to seat anybody.
    this.reserved[i] = o?.reserved ? 1 : 0;
    this.turnRng[i] = mulberry32(turnSeed);
    this.driving++;

    this.graph.sampleAt(eid, s, this._sample);
    this.x[i] = this._sample.x;
    this.z[i] = this._sample.z;
    this.y[i] = this.heightAt(this._sample.x, this._sample.z);
    this.yaw[i] = yawFor(this._sample.dx, this._sample.dz);

    if (this.fleet) {
      this.handle[i] = this.fleet.addCar(this.archKeys[arch], {
        paint,
        x: this.x[i],
        y: this.y[i],
        z: this.z[i],
        yaw: this.yaw[i],
      });
    }
    // LAST: the slot is fully live, so a director may read its pose and seat
    // people in it from inside the callback.
    this.onActivate?.(i);
  }

  /** @param {number} i */
  _deactivate(i) {
    if (this.state[i] === CAR_STATE.FREE) return;
    // FIRST: the slot is still readable, so a director can find whoever it put
    // in this car before the state is torn down.
    this.onDeactivate?.(i);
    if (this.claim[i] >= 0) {
      this.claimOwner[this.claim[i]] = -1;
      this.claim[i] = -1;
      this.claimT[i] = 0;
    }
    if (this.handle[i] >= 0) {
      this.fleet?.removeCar(this.handle[i]);
      this.handle[i] = -1;
    }
    this.state[i] = CAR_STATE.FREE;
    this.edge[i] = -1;
    this.nextEdge[i] = -1;
    this.want[i] = -1;
    // The response's three bytes go with the slot, never into its next tenant:
    // an ambient saloon that inherited PURSUIT would drive at the player at
    // 1.35x with a queue-jump pass, and nothing upstream would explain it.
    this.mode[i] = DRIVE_MODE.AMBIENT;
    this.priority[i] = 0;
    this.reserved[i] = 0;
    this.turnRng[i] = null;
    this.driving--;
  }

  // ── internals: driving ────────────────────────────────────────────────────

  /**
   * Chooses the successor edge and, once close enough, joins the queue for the
   * junction box. Straight on wins {@link TRAFFIC.straightBias} of the time;
   * the rest is split evenly across the turns.
   * @param {number} i
   */
  _planAhead(i) {
    const g = this.graph;
    const e = g.edges[this.edge[i]];
    if (this.nextEdge[i] < 0) {
      const outs = g.outgoing(e.to, e.id);
      this.nextEdge[i] = outs.length ? this._chooseNext(i, outs) : -1;
    }
    const nxt = this.nextEdge[i] >= 0 ? g.edges[this.nextEdge[i]] : null;

    // Only a LINK into a real junction box is worth queueing for.
    const node = nxt && nxt.kind === 'link' ? nxt.node : -1;
    const dist = e.length - this.s[i];
    if (e.kind === 'lane' && node >= 0 && dist <= TRAFFIC.claimDist) {
      if (this.want[i] !== node) {
        this.want[i] = node;
        this.ticket[i] = this._ticket++;
      }
      if (this.state[i] === CAR_STATE.CRUISE) this.state[i] = CAR_STATE.APPROACH;
    } else if (e.kind === 'lane' && this.state[i] === CAR_STATE.APPROACH && node < 0) {
      this.state[i] = CAR_STATE.CRUISE;
      this.want[i] = -1;
    }
  }

  /**
   * @param {number} i @param {number[]} outs
   * @returns {number}
   */
  _chooseNext(i, outs) {
    const g = this.graph;
    // PURSUIT navigates; it does not roll. Drawing the turn number anyway and
    // throwing it away would be tidier-looking and pointless: `turnRng` is a
    // PER-CAR stream, so a pursuit car skipping a draw cannot perturb anybody
    // else's route — and the car itself only exists because of something the
    // player did.
    if (this.mode[i] === DRIVE_MODE.PURSUIT) return this._pursuitNext(i, outs);
    const rng = this.turnRng[i];
    const roll = rng ? rng() : 0.5;
    if (outs.length === 1) return outs[0];
    let straight = -1;
    for (const id of outs) if (g.edges[id].turn === 0 && !g.edges[id].uTurn) straight = id;
    if (straight >= 0 && roll < TRAFFIC.straightBias) return straight;
    // The remainder is split over everything that is not the straight-on.
    const rest = outs.filter((id) => id !== straight);
    if (rest.length === 0) return straight >= 0 ? straight : outs[0];
    const u = straight >= 0 ? (roll - TRAFFIC.straightBias) / (1 - TRAFFIC.straightBias) : roll;
    return rest[Math.min(rest.length - 1, (u * rest.length) | 0)];
  }

  /**
   * The pursuit turn: whichever successor puts the car NEAREST its goal.
   *
   * Two details do the work. It scores the LANE the car ends up on rather than
   * the link it takes to get there — every fillet out of one junction ends
   * within a metre of the same box, so scoring links compares four almost
   * identical numbers and the choice becomes noise. And ties break to the
   * lowest edge id, which with no rng at all makes a chase a pure function of
   * (graph, goal): two runs of one pursuit take the same corners.
   *
   * It is deliberately GREEDY and has no map. A cruiser can therefore circle a
   * block while the player stands in the middle of it, which is the correct
   * behaviour anyway — the crew gets out and walks the last few metres.
   *
   * @param {number} i @param {number[]} outs
   * @returns {number}
   */
  _pursuitNext(i, outs) {
    const g = this.graph;
    const gx = this.goalX[i];
    const gz = this.goalZ[i];
    let best = -1;
    let bestD2 = Infinity;
    for (let k = 0; k < outs.length; k++) {
      const e = g.edges[outs[k]];
      if (!e) continue;
      const exit = e.kind === 'link' && e.next.length ? g.edges[e.next[0]] : e;
      const p = exit.points[exit.points.length - 1];
      const dx = p.x - gx;
      const dz = p.z - gz;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = outs[k];
      }
    }
    return best >= 0 ? best : outs[0];
  }

  /**
   * Moves a car onto its chosen successor, carrying the arc-length overshoot so
   * a long step never loses distance. Claims are taken and released here and
   * nowhere else.
   * @param {number} i
   * @returns {boolean} False when the car was retired.
   */
  _advanceEdge(i) {
    const g = this.graph;
    const cur = g.edges[this.edge[i]];
    const over = this.s[i] - cur.length;
    let nxt = this.nextEdge[i];
    if (nxt < 0) {
      const outs = g.outgoing(cur.to, cur.id);
      nxt = outs.length ? this._chooseNext(i, outs) : -1;
    }
    if (nxt < 0) {
      this._deactivate(i);
      return false;
    }
    const ne = g.edges[nxt];

    if (ne.kind === 'link' && ne.node >= 0) {
      // No claim, no entry. The stop curve should already have parked us short
      // of the line; this is the belt to that braces.
      if (this.claim[i] !== ne.node) {
        this.s[i] = cur.length - 0.01;
        return false;
      }
      this.state[i] = CAR_STATE.CROSS;
    } else if (cur.kind === 'link' && this.claim[i] >= 0) {
      this.claimOwner[this.claim[i]] = -1;
      this.claim[i] = -1;
      this.claimT[i] = 0;
      this.state[i] = CAR_STATE.CRUISE;
      this.want[i] = -1;
      this.sig[i] = 0;
    }

    this.edge[i] = nxt;
    this.s[i] = over;
    this.nextEdge[i] = -1;
    // The approach curve is asymptotic and the step is discrete, so a car
    // arrives a few centimetres per second OVER the new limit. Trimming it here
    // makes "nobody ever exceeds their lane's limit" an exact invariant instead
    // of one with an epsilon, at the cost of a 0.05 m/s jolt nobody can see.
    // The pursuit scale rides along, or the trim would cancel the boost at
    // every single edge change.
    const lim = ne.speedLimit * this._speedScale(i);
    if (this.v[i] > lim) this.v[i] = lim;
    return true;
  }

  /**
   * LIVENESS. A claim is a promise to cross promptly, and a car can be stopped
   * from crossing by something the junction protocol knows nothing about — a
   * player standing in the mouth, a carjack hold. After
   * {@link TRAFFIC.claimTimeout} seconds of holding without entering the box,
   * the claim goes back in the pool and the car re-queues with a fresh ticket.
   * Without this one rule a single parked pedestrian can freeze a crossroads
   * for the rest of the session.
   * @param {number} dt
   */
  _expireClaims(dt) {
    for (let i = 0; i < this.top; i++) {
      if (this.claim[i] < 0) continue;
      if (this.state[i] === CAR_STATE.CROSS) {
        this.claimT[i] = 0;
        continue;
      }
      this.claimT[i] += dt;
      if (this.claimT[i] < TRAFFIC.claimTimeout) continue;
      this.claimOwner[this.claim[i]] = -1;
      this.claim[i] = -1;
      this.claimT[i] = 0;
      this.ticket[i] = this._ticket++;
    }
  }

  /**
   * FIFO grant pass. One node, one car; the lowest ticket wins; and nothing is
   * granted into an exit lane that has no room (see the header).
   *
   * ⚠ A car with {@link priority} (a pursuit unit at ★3+) wins the QUEUE
   * outright, and among themselves priority cars are still FIFO. That is the
   * whole override: the selection half below decides who asks, the grant half
   * further down is untouched, so "one car in the box, and only into an exit
   * that has room" survives verbatim. Blue lights buy you the front of the
   * queue, never a collision.
   */
  _grantClaims() {
    const g = this.graph;
    this._stamp++;
    const stamp = this._stamp;

    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i)) continue;
      const n = this.want[i];
      if (n < 0 || this.claim[i] >= 0 || this.claimOwner[n] >= 0) continue;
      if (this.hold[i]) continue;
      const pri = this.priority[i];
      const better = this._bestStamp[n] !== stamp
        || pri > this._bestPri[n]
        || (pri === this._bestPri[n] && this.ticket[i] < this._bestTicket[n]);
      if (!better) continue;
      this._bestStamp[n] = stamp;
      this._bestCar[n] = i;
      this._bestTicket[n] = this.ticket[i];
      this._bestPri[n] = pri;
    }

    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i)) continue;
      const n = this.want[i];
      if (n < 0 || this.claim[i] >= 0) continue;
      if (this._bestStamp[n] !== stamp || this._bestCar[n] !== i) continue;
      const link = this.nextEdge[i] >= 0 ? g.edges[this.nextEdge[i]] : null;
      if (!link || link.node !== n) continue;
      const exit = link.next.length ? g.edges[link.next[0]] : null;
      if (exit && !this._exitHasRoom(exit)) continue;
      this.claimOwner[n] = i;
      this.claim[i] = n;
      this.claimT[i] = 0;
    }
  }

  /**
   * @param {import('./LaneGraph.js').LaneEdge} exit
   * @returns {boolean} True when nothing is parked on the exit lane's first
   *   {@link TRAFFIC.exitClear} metres.
   */
  _exitHasRoom(exit) {
    for (let i = 0; i < this.top; i++) {
      if (!this._isDriving(i) || this.edge[i] !== exit.id) continue;
      if (this.s[i] < TRAFFIC.exitClear) return false;
    }
    return true;
  }

  /**
   * Distance from car `i`'s nose to the nearest car ahead of it, looking one
   * edge downstream so a queue does not lose its leader across a junction.
   * @param {number} i
   * @returns {number} Metres, or `Infinity`.
   */
  _leaderGap(i) {
    const mine = this.edge[i];
    const myS = this.s[i];
    let best = Infinity;
    for (let j = 0; j < this.top; j++) {
      if (j === i || !this._isDriving(j)) continue;
      if (this.edge[j] === mine) {
        const d = this.s[j] - myS;
        if (d > 0 && d < best) best = d;
      }
    }
    if (best < Infinity) return best;

    const nxt = this.nextEdge[i];
    if (nxt < 0) return Infinity;
    const rest = this.graph.edges[mine].length - myS;
    for (let j = 0; j < this.top; j++) {
      if (j === i || !this._isDriving(j) || this.edge[j] !== nxt) continue;
      const d = rest + this.s[j];
      if (d < best) best = d;
    }
    return best;
  }

  /**
   * How far ahead the nearest blocking actor is, ALONG THE LANE. A projection,
   * not a radius: a pedestrian on the pavement beside the car is not in the
   * way, and braking for one reads as a bug. The caller turns the distance into
   * a brake curve, so a car slows for a person rather than stamping on it.
   * @param {number} i
   * @returns {number} Metres, or `Infinity`.
   */
  _actorAhead(i) {
    // Heading for yaw: Ry maps +X to (cos, -sin).
    const fx = Math.cos(this.yaw[i]);
    const fz = -Math.sin(this.yaw[i]);
    let best = Infinity;
    for (let k = 0; k < this._actors.length; k++) {
      const a = this._actors[k];
      if (!a.on) continue;
      const dx = a.x - this.x[i];
      const dz = a.z - this.z[i];
      const along = dx * fx + dz * fz;
      if (along <= 0 || along > TRAFFIC.actorSee + a.r) continue;
      const lat = Math.abs(dx * -fz + dz * fx);
      if (lat >= TRAFFIC.actorLateral + a.r) continue;
      const d = along - a.r;
      if (d < best) best = d;
    }
    return best;
  }

  /**
   * Position `look` metres ahead of the car, walking into the successor edge
   * when the current one runs out — a lookahead that saturates at the edge end
   * makes every car straighten up just before a corner.
   * @param {number} i @param {number} look
   * @param {{x: number, z: number, dx: number, dz: number}} out
   */
  _lookAhead(i, look, out) {
    const g = this.graph;
    const e = g.edges[this.edge[i]];
    const ahead = this.s[i] + look;
    if (ahead <= e.length) {
      g.sampleAt(e.id, ahead, out);
      return;
    }
    let nxt = this.nextEdge[i];
    if (nxt < 0) {
      const outs = g.outgoing(e.to, e.id);
      nxt = outs.length ? outs[0] : -1;
    }
    if (nxt < 0) {
      g.sampleAt(e.id, e.length, out);
      return;
    }
    g.sampleAt(nxt, ahead - e.length, out);
  }

  /**
   * Arms the indicator {@link TRAFFIC.signalLead} seconds before the turn and
   * holds it until the turn completes (the claim release clears it).
   * @param {number} i
   */
  _updateSignal(i) {
    if (this.state[i] === CAR_STATE.CROSS) return; // held from the approach
    const nxt = this.nextEdge[i] >= 0 ? this.graph.edges[this.nextEdge[i]] : null;
    if (!nxt || nxt.kind !== 'link' || nxt.turn === 0) {
      this.sig[i] = 0;
      return;
    }
    const e = this.graph.edges[this.edge[i]];
    const dist = e.length - this.s[i];
    const eta = dist / Math.max(1.5, this.v[i]);
    this.sig[i] = eta <= TRAFFIC.signalLead ? nxt.turn : 0;
  }

  // ── internals: parking ────────────────────────────────────────────────────

  /**
   * Every legal parking bay in the level, in a fixed order. Bays sit on the
   * kerb side of each lattice road, {@link TRAFFIC.parkSpacing} apart.
   *
   * ⚠ Bays are walked along the edge's PAVED RECT, not its centre line. A
   * `RoadEdge`'s rect already excludes the intersection patches at both ends
   * (`levels/city/roads.js#makeEdge`), so "not in a junction box" comes out of
   * the data rather than out of a margin — and the margin that IS applied only
   * has to clear the zebra and the stop bar, which are painted flush against
   * the mouth. Measuring from the crossing CENTRE instead put cars inside the
   * boulevard junctions, whose boxes are 11 m deep.
   *
   * ⚠ AND THE TWO LISTS ARE NOT THE SAME RULE. A city bay STRADDLES the kerb —
   * its centre is on the tarmac by {@link TRAFFIC.parkKerbInset} and half the
   * body overhangs the pavement; a country bay is fully OFF the road, on the
   * grass. See
   * {@link TRAFFIC.parkCountryStride} for why, and {@link _vergeBay} for the
   * clearance + terrain test each rural candidate has to pass.
   *
   * @returns {{city: Array<{x: number, z: number, yaw: number}>,
   *   country: Array<{x: number, z: number, yaw: number, cls: string}>}}
   *   `country` carries its road's lane CLASS too (`'country'`, or a
   *   {@link TIER_CLASSES} member) — `_buildBays` reads it to keep dirt
   *   verges quiet and truck-only, the same rule the driving ring's
   *   `_activate` applies. City bays carry no `cls`: `parkStride` treats
   *   every lattice class alike.
   */
  _parkingSlots() {
    /** @type {Array<{x: number, z: number, yaw: number}>} */
    const out = [];
    /** @type {Array<{x: number, z: number, yaw: number}>} */
    const rural = [];
    const lattice = this.map?.roads?.lattice ?? [];
    const margin = TRAFFIC.parkJunctionMargin;

    for (const e of lattice) {
      // ⚠ NO BAY ON AN ALLEY, and the size gate below would NOT have caught it:
      // a 4.5 m alley leaves `off` = 2.25 − `parkKerbInset` = 2.05, comfortably
      // over the 1 m floor, so Fairview would have grown two rows of parked cars
      // down every pedestrian cut in town — reachable by nothing, because
      // `LaneGraph` refuses the same class. See `NO_TRAFFIC_CLASSES`.
      if (NO_TRAFFIC_CLASSES.has(e.class)) continue;
      // An 'x' line has a constant x and RUNS along Z (`roads.js`).
      const alongZ = e.axis === 'x';
      const runLen = alongZ ? e.d : e.w;
      const usable = runLen - margin * 2;
      if (usable < TRAFFIC.parkSpacing) continue;
      const run0 = alongZ ? e.z : e.x;
      const centre = alongZ ? e.x + e.w / 2 : e.z + e.d / 2;
      const off = e.width / 2 - TRAFFIC.parkKerbInset;
      if (off < 1) continue;
      const n = Math.floor(usable / TRAFFIC.parkSpacing);
      for (let k = 0; k <= n; k++) {
        const t = run0 + margin + (usable * k) / Math.max(1, n);
        for (const side of [1, -1]) {
          // The kerb is on the RIGHT of the direction you drive, so a car
          // parked against it faces that direction (`side` picks which).
          const dx = alongZ ? 0 : side;
          const dz = alongZ ? side : 0;
          out.push({
            x: alongZ ? centre - dz * off : t,
            z: alongZ ? t : centre + dx * off,
            yaw: yawFor(dx, dz),
          });
        }
      }
    }

    // A handful on the country VERGES — a different rule from the kerbs above.
    const country = this.map?.roads?.country ?? [];
    const sea = (this.map?.seaLevel ?? 0) + TRAFFIC.parkVergeDry;
    for (const r of country) {
      if (r.kind === 'head' || !r.points || r.points.length < 5) continue;
      // The same mapping `LaneGraph.js` uses to turn a route KIND into a lane
      // CLASS (`TIER_CLASSES.has(r.kind) ? r.kind : 'country'`) — imported
      // rather than restated, because `TIER_CLASSES` is the exported contract
      // and a second copy of the SET would be the `links.js` /
      // `countryroads.js` duplicated-constant trap.
      const cls = TIER_CLASSES.has(r.kind) ? r.kind : 'country';
      const off = r.width / 2 + TRAFFIC.parkVergeClear;
      // Every 6th sample, and never the first or last two: a route's ends are a
      // city join or a rounded route-to-route corner, and both want clear
      // verges. `side` +1 is the right-hand one, so the car faces the way the
      // traffic beside it drives.
      for (let k = 2; k + 2 < r.points.length; k += 6) {
        const a = r.points[k];
        const b = r.points[k + 1];
        const l = Math.hypot(b.x - a.x, b.z - a.z);
        if (l < 1e-3) continue;
        const dx = (b.x - a.x) / l;
        const dz = (b.z - a.z) / l;
        for (const side of [1, -1]) {
          const bay = this._vergeBay(r, a.x - dz * off * side, a.z + dx * off * side, sea, country);
          if (bay) rural.push({ x: bay.x, z: bay.z, yaw: yawFor(dx * side, dz * side), cls });
        }
      }
    }
    return { city: out, country: rural };
  }

  /**
   * Accepts or rejects one country VERGE candidate.
   *
   * Two independent tests, and both were paid for in the playtest:
   *  • CLEARANCE is measured against whole POLYLINES, not against the offset
   *    that produced the point. On the inside of a bend the NEXT segment is
   *    nearer than the one the bay was built from, so an offset that looks
   *    clear can still put a wing over the tarmac.
   *  • the GROUND has to be dry and flat. `heightAt` is the level's own sampler
   *    (a headless `Traffic` gets the `() => 0` default and every bay passes,
   *    which is what keeps `tests/traffic.test.mjs` measuring geometry rather
   *    than terrain).
   *
   * ⚠ AGAINST EVERY ROUTE, NOT JUST ITS OWN, and that was a real hole. This
   * only ever checked the route the bay was offset from, which is right for a
   * lone country lane and wrong the moment two roads cross: measured on the
   * shipped three-city network BEFORE the road tiers, 105 of 1,075 verge bays
   * (9.8%) stood inside SOME other route's carriageway, up to 3.4 m in. The
   * tiers made it unmissable — a 16 m ring road crosses every radial on the
   * island, and a bay 7.9 m inside it is a parked car in the fastest travel
   * lane in the level. The contract was always the wider one:
   * `tests/traffic.test.mjs` section (i) has measured `toRibbon` over EVERY
   * country route since it was written, and passed only because a one-city
   * fixture has no crossings in it.
   *
   * Cul-de-sac discs count too — a turning circle is tarmac like any other.
   *
   * @param {any} r The country route this candidate was offset from.
   * @param {number} x @param {number} z Candidate bay centre.
   * @param {number} sea Waterline plus the dry margin.
   * @param {any[]} [all] Every country route. Defaults to just `r`, so a caller
   *   with one road in hand still gets the old behaviour.
   * @returns {{x: number, z: number}|null}
   */
  _vergeBay(r, x, z, sea, all = null) {
    for (const q of all ?? [r]) {
      const need = (q.width ?? 0) / 2 + TRAFFIC.parkVergeMin;
      if (q.kind === 'head') {
        if (q.head && Math.hypot(x - q.head.x, z - q.head.z) < q.head.r + TRAFFIC.parkVergeMin) return null;
        continue;
      }
      const pts = q.points;
      if (!pts || pts.length < 2) continue;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        // Cheap reject before the projection: a bay is `parkVergeClear` off its
        // OWN road and hundreds of metres from most of the others.
        if (Math.min(a.x, b.x) - need > x || x > Math.max(a.x, b.x) + need) continue;
        if (Math.min(a.z, b.z) - need > z || z > Math.max(a.z, b.z) + need) continue;
        const ex = b.x - a.x;
        const ez = b.z - a.z;
        const len2 = ex * ex + ez * ez;
        let u = len2 > 1e-12 ? ((x - a.x) * ex + (z - a.z) * ez) / len2 : 0;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        if (Math.hypot(x - (a.x + ex * u), z - (a.z + ez * u)) < need) return null;
      }
    }

    const e = TRAFFIC.parkVergeProbe;
    const h = this.heightAt(x, z);
    const hxp = this.heightAt(x + e, z);
    const hxn = this.heightAt(x - e, z);
    const hzp = this.heightAt(x, z + e);
    const hzn = this.heightAt(x, z - e);
    if (Math.min(h, hxp, hxn, hzp, hzn) <= sea) return null;
    const grade = Math.max(Math.abs(hxp - hxn), Math.abs(hzp - hzn)) / (2 * e);
    if (grade > TRAFFIC.parkVergeGrade) return null;
    return { x, z };
  }

  /**
   * @param {number} i @param {number} x @param {number} z @param {number} yaw
   * @param {number} arch @param {number} paint Index into `PAINTS`.
   */
  _initParked(i, x, z, yaw, arch, paint) {
    const y = this.heightAt(x, z);
    this.pState[i] = CAR_STATE.PARKED;
    this.pX[i] = x;
    this.pY[i] = y;
    this.pZ[i] = z;
    this.pYaw[i] = yaw;
    this.pArch[i] = arch;
    this.pPaint[i] = paint;
    // A NEW CAR IS NOT A WRECK. This slot may have held one a minute ago — the
    // pool is recycled and the bytes are not — and a bay that comes back
    // burnt-out is a car nobody can get into with nothing on screen to say why.
    // Cleared HERE as well as in `_freeParked` on purpose: two independent
    // clears mean the byte cannot survive a round trip through either door.
    this.pWreck[i] = 0;
    this.parked++;

    if (this.fleet) {
      this.pHandle[i] = this.fleet.addCar(this.archKeys[arch], { paint: PAINTS[paint], x, y, z, yaw });
    }
    this.pDoor[i] = this._doorOf(i, arch);
    this._setParkedColliders(i, true);
  }

  /**
   * Driver-door handle position in CAR-LOCAL metres (−Z is the driver's side —
   * `carSpec.js`). Read from the archetype's real `carLayout` when a fleet is
   * present; derived from the spec otherwise, which is all a headless sim needs.
   * @param {number} i @param {number} arch
   * @returns {{x: number, z: number}}
   */
  _doorOf(i, arch) {
    const out = this.doorLocal(arch, 0, { x: 0, z: 0 });
    const info = this.pHandle[i] >= 0 ? this.fleet?.infoOf(this.pHandle[i]) : null;
    const front = info?.layout?.doors?.find?.((d) => d.key === 'f') ?? null;
    if (front) out.x = (front.x0 + front.x1) / 2;
    return out;
  }

  /**
   * Registers (or hides) the two circles a parked car occupies.
   *
   * ⚠ NOT the axle midpoints, which is the obvious choice and the wrong one: at
   * `W/2` they leave a hole between them big enough to stand in, and the first
   * thing a player does with a parked car is walk at its middle. The circles sit
   * at `±L·PARK_CIRCLE.at` with radius `W/2 + PARK_CIRCLE.fat`, which is the
   * cheapest pair that OVERLAPS in the middle and still reaches both bumpers.
   * The cost is ~25 cm of extra width per flank — an actor brushing past feels
   * the car slightly early, which reads as bodywork rather than as a bug.
   * @param {number} i @param {boolean} on
   */
  _setParkedColliders(i, on) {
    const grid = this.collision;
    if (!grid || typeof grid.addCircle !== 'function') return;
    const ch = this.archChassis[this.pArch[i]];
    const r = ch.W / 2 + PARK_CIRCLE.fat;
    const c = Math.cos(this.pYaw[i]);
    const sn = Math.sin(this.pYaw[i]);
    const along = ch.L * PARK_CIRCLE.at;
    const pts = [along, -along];
    const ids = [this.pCol0, this.pCol1];
    for (let k = 0; k < 2; k++) {
      const wx = this.pX[i] + pts[k] * c;
      const wz = this.pZ[i] - pts[k] * sn;
      let id = ids[k][i];
      if (id < 0) {
        if (!on) continue;
        id = grid.addCircle(wx, wz, r, 'vehicle', { y: this.pY[i], h: 1.6 });
        ids[k][i] = id;
      } else if (typeof grid.setCircle === 'function') {
        grid.setCircle(id, wx, wz, r);
      }
      grid.setShapeEnabled?.(id, on);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything the sim needs about a car's body, derived from its SPEC alone —
 * the same two lines `carBuild.js#carLayout` uses for its axle stations, so
 * this never needs a geometry build (and `tests/traffic.test.mjs` pins the two
 * against each other).
 *
 * @param {import('../vehicles/carSpec.js').CarSpec} spec
 * @returns {{L: number, W: number, wheelR: number, fa: number, ra: number, wheelbase: number}}
 */
export function chassisOf(spec) {
  const fa = spec.L / 2 - spec.overF;
  const ra = -spec.L / 2 + spec.overR;
  return { L: spec.L, W: spec.W, wheelR: spec.wheelR, fa, ra, wheelbase: fa - ra };
}

/**
 * Highest speed from which `vEnd` is still reachable in `gap` metres at
 * {@link TRAFFIC.curveBrake} — `v² = vEnd² + 2·a·d`. The decel is deliberately
 * below the car's real braking ability, which is what leaves room to recover
 * when the leader brakes harder than planned.
 *
 * With `vEnd = 0` it is a stop curve (a junction line, a leader's tail); with
 * `vEnd` set it is the approach to a SLOWER edge, which is what stops a car
 * carrying 12 m/s of boulevard into a 5.5 m/s right turn.
 *
 * @param {number} gap Metres of usable space.
 * @param {number} [vEnd=0] Speed wanted at the end of the gap.
 * @returns {number} m/s.
 */
function brakeCurve(gap, vEnd = 0) {
  if (gap <= 0) return vEnd;
  return Math.sqrt(vEnd * vEnd + 2 * TRAFFIC.curveBrake * gap);
}

/** @param {number} a @returns {number} Radians in (−π, π]. */
function wrapPi(a) {
  let v = a;
  while (v > Math.PI) v -= Math.PI * 2;
  while (v < -Math.PI) v += Math.PI * 2;
  return v;
}

/** @param {number} v @param {number} a @param {number} b */
function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}
